import React, { useEffect, useRef, useState } from "react";
import api from "../setupAxios";
import TopBar from "../components/TopBar";
import Badge from "../components/Badge";
import { onActualizarPanel } from "../statsBus";
import { useDialog } from "../context/dialogContext";
import { devolverConAvisoRevision } from "../revision";
import Alertas from "../components/Alertas";
import { tonoEstado } from "../estados";
import { isoToEs } from "../fechas";
import { useFiltroColumnas, FilterHead, PanelHead } from "../components/TableFilter";
import ComponenteCampos, { ICONO_COMPONENTE } from "../components/ComponenteCampos";
import FichaLlaveModal from "../components/FichaLlave";
import InfoLlaveModal from "../components/InfoLlaveModal";
import useNavegacionLista from "../components/useNavegacionLista";
import { usePaginacion, Paginador } from "../components/Paginacion";
import { tomarListaConteo, onListaConteo } from "../listaConteo";
import PisoAutocomplete, { textoPiso } from "../components/PisoAutocomplete";
import { HistorialPisoModal, HistorialGeneralModal, PendientesCopiaModal, FotoPiso } from "../components/PisoPaneles";

// Fecha de hoy en ISO (huso del navegador) — la devolución estimada nunca
// puede ser anterior a hoy.
const hoyIso = () => new Date().toISOString().slice(0, 10);

// Panel de administración (sólo rol 'llaves').
//
// Cada panel se monta UNA sola vez, la primera vez que se visita su pestaña,
// y a partir de ahí se queda montado (solo oculto con CSS) en vez de
// desmontarse al cambiar de pestaña. Antes, cambiar de pestaña y volver
// repetía la consulta entera cada vez (con tablas grandes, como Inventario,
// se notaba mucho); ahora solo carga una vez y cambiar de pestaña es
// instantáneo — para refrescar datos ya cargados está el botón "Actualizar"
// de la barra superior.
const PANELES = [
  ["dinamico", "📊 Panel dinámico", Dinamico],
  ["alertas", "🔔 Alertas", Alertas],
  ["inventario", "🔑 Inventario llaves", Inventario],
  ["conteo", "🔢 Contar cajetines", ContarCajetines],
  ["crear_ruta", "📝 Crear ruta", CrearRutaAdmin],
  ["personal", "👥 Personal", Personal],
  ["clientes", "🏠 Clientes", Clientes],
  ["compras", "🧾 Compras", Compras],
  ["departamentos", "🏷️ Departamentos", Departamentos],
];

// Panel abierto: se guarda en la dirección (/admin#inventario), así que se
// conserva al recargar; y también en el navegador, para volver al último
// panel al entrar en Administración desde otra pantalla.
const CLAVE_PANEL = "admin_panel";
const panelValido = (k) => PANELES.some(([p]) => p === k);
function panelInicial() {
  const h = decodeURIComponent((window.location.hash || "").slice(1));
  if (panelValido(h)) return h;
  try { const g = localStorage.getItem(CLAVE_PANEL); if (panelValido(g)) return g; } catch { /* sin almacenamiento */ }
  return "dinamico";
}

export default function Admin() {
  const [tab, setTab] = useState(panelInicial);
  const [visitados, setVisitados] = useState(() => new Set([panelInicial()]));
  // Contador de alertas nuevas en la pestaña "🔔 Alertas" (lo carga TopBar).
  const [nAlertas, setNAlertas] = useState(0);
  useEffect(() => {
    const f = (e) => setNAlertas(e.detail?.nuevas || 0);
    window.addEventListener("alertas:resumen", f);
    return () => window.removeEventListener("alertas:resumen", f);
  }, []);

  // Pisos que necesitan copias (pestaña "📊 Panel dinámico" en rojo, como Alertas).
  const [nCopias, setNCopias] = useState(0);
  const cargarCopias = () => api.get("/pisos_pendientes_copia").then(({ data }) => setNCopias((data.rows || []).length)).catch(() => {});
  useEffect(() => { cargarCopias(); }, []);
  useActualizarGlobal(cargarCopias);
  useEffect(() => {
    const f = (e) => setNCopias(e.detail?.pisos || 0);
    window.addEventListener("copias:resumen", f);
    return () => window.removeEventListener("copias:resumen", f);
  }, []);

  useEffect(() => {
    setVisitados((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
    if (window.location.hash.slice(1) !== tab) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${tab}`);
    }
    try { localStorage.setItem(CLAVE_PANEL, tab); } catch { /* sin almacenamiento */ }
  }, [tab]);
  // Si se cambia la dirección a mano (#compras…), abrir ese panel.
  useEffect(() => {
    const alCambiar = () => { const h = decodeURIComponent(window.location.hash.slice(1)); if (panelValido(h)) setTab(h); };
    window.addEventListener("hashchange", alCambiar);
    return () => window.removeEventListener("hashchange", alCambiar);
  }, []);
  // Otra pestaña manda una lista de cajetines a contar -> abrir "Contar cajetines".
  useEffect(() => onListaConteo(() => setTab("conteo")), []);

  return (
    <div className="app-shell">
      <TopBar />
      <div className="subnav">
        {PANELES.map(([k, label]) => (
          <button key={k} className={[tab === k ? "active" : "", (k === "alertas" && nAlertas > 0) || (k === "dinamico" && nCopias > 0) ? "alerta" : ""].filter(Boolean).join(" ")}
                  onClick={() => setTab(k)}>
            {label}{k === "alertas" && nAlertas > 0 && <span className="tab-badge">{nAlertas}</span>}
            {k === "dinamico" && nCopias > 0 && <span className="tab-badge" title="Pisos que necesitan copias">{nCopias}</span>}
          </button>
        ))}
      </div>
      {PANELES.map(([k, , Comp]) => (
        visitados.has(k) && (
          <div key={k} style={{ display: tab === k ? "block" : "none" }}>
            <Comp />
          </div>
        )
      ))}
    </div>
  );
}

// Ejecuta `cargar` al montar y, después, con un pequeño debounce cada vez que
// cambien `deps` (evita disparar una consulta por cada tecla al buscar).
function useAutoBuscar(cargar, deps) {
  useEffect(() => {
    const t = setTimeout(cargar, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Suscribe `cargar` al botón "Actualizar" global de la barra superior, para
// que refresque este mismo panel sin recargar la página (y sin perder en
// qué pestaña está el usuario).
function useActualizarGlobal(cargar) {
  useEffect(() => onActualizarPanel(cargar), [cargar]);
}

// Pagina un array ya filtrado. Con listas grandes (Inventario puede tener
// varios miles de llaves) lo que de verdad ralentiza la pantalla no es la
// consulta a la BBDD (unos cientos de ms) sino pintar de golpe miles de
// filas en el DOM — así que en vez de listar TODO, solo se montan las filas
// de la página actual. `resetDeps` son las dependencias que deben volver a
// la página 1 (los propios datos y los filtros de columna).
// Filtro de estado (activos/bajas/…) como grupo de botones, en vez de un
// <select>: se ve de un vistazo cuál está activo y es más rápido de usar.
function FiltroVer({ value, onChange, opciones }) {
  return (
    <div className="seg-buttons">
      {opciones.map(([v, label]) => (
        <button key={v} className={value === v ? "on" : ""} data-tone={tonoEstado(v)} onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

// -------------------------------------------------------------- PANEL DINÁMICO
// Absorbe lo que antes era la pestaña "Pisos" (cajetín + estado activo/baja):
// son los mismos pisos con casi los mismos datos, así que en vez de dos
// tablas casi iguales hay una sola con todo (salvo "empresa", que aquí no
// hacía falta mostrar).
function Dinamico() {
  const [rows, setRows] = useState([]);
  const [ver, setVer] = useState("activos");
  const [editNec, setEditNec] = useState(null);       // {pid, valor} — edición en línea de "Necesarias"
  const [editCaj, setEditCaj] = useState(null);       // {pid, valor} — edición en línea de "Cajetín"
  const [revisarModal, setRevisarModal] = useState(null); // {pid, codigo, valor, nota}
  const [infoPiso, setInfoPiso] = useState(null);     // {pid, codigo} — modal de configuración de llaves
  const [soloRevisar, setSoloRevisar] = useState(false);
  const [histPiso, setHistPiso] = useState(null);     // {pid, codigo} — 📜 historial de un piso
  const [histGeneral, setHistGeneral] = useState(false);
  const [pendCopia, setPendCopia] = useState(false);
  const { avisar, confirmar } = useDialog();
  const cargar = () => api.get("/dinamico", { params: { ver } }).then(({ data }) => {
    const rs = data.rows || [];
    setRows(rs);
    window.dispatchEvent(new CustomEvent("copias:resumen", { detail: { pisos: rs.filter((r) => r.activo && r.falta > 0).length } }));
  });
  useAutoBuscar(cargar, [ver]);
  useActualizarGlobal(cargar);

  const guardarNecesarias = async (pid, valor) => {
    const v = valor.trim();
    await api.post("/piso_ajuste", { piso_id: pid, campo: "necesarias", valor: v === "" ? null : v });
    setEditNec(null);
    cargar();
  };
  const guardarRevisar = async () => {
    await api.post("/piso_ajuste", {
      piso_id: revisarModal.pid, campo: "revisar",
      valor: revisarModal.valor, nota: revisarModal.nota,
    });
    setRevisarModal(null);
    cargar();
  };
  const guardarCajetin = async (pid, valor, confirmado = false) => {
    const cajetin = valor.trim();
    if (!cajetin) return;
    try {
      const { data } = await api.post("/piso/cajetin", { piso_id: pid, cajetin, confirmado });
      if (data.ok) { setEditCaj(null); cargar(); }
      else if (data.requiere_confirmacion) { if (await confirmar(data.error)) guardarCajetin(pid, valor, true); }
      else avisar(data.error || "No se pudo asignar.");
    } catch (err) {
      const d = err.response?.data;
      if (d?.requiere_confirmacion) { if (await confirmar(d.error)) guardarCajetin(pid, valor, true); }
      else avisar(d?.error || "No se pudo asignar.");
    }
  };

  const campos = [
    ["id_piso", "ID piso", (r) => r.pid],
    ["piso", "Piso", (r) => r.codigo || r.pid],
    ["cajetin", "Cajetín", (r) => r.cajetin],
    ["reales", "Reales", (r) => r.reales],
    ["necesarias", "Necesarias", (r) => r.necesarias],
    ["prestadas", "Prestadas", (r) => r.prestadas],
    ["en_cajetin", "En cajetín", (r) => r.en_cajetin],
    ["sin_codigo", "Sin código", (r) => r.sin_codigo],
    ["estado", "Estado", (r) => (r.activo ? "activo" : "baja")],
  ];
  const pisosCopias = rows.filter((r) => r.activo && r.falta > 0).length;
  const rowsBase = soloRevisar ? rows.filter((r) => r.revisar) : rows;
  const { filtros, setFiltro, limpiar, filtradas } = useFiltroColumnas(rowsBase, campos);
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(filtradas, [rowsBase, filtros]);

  return (
    <div className="card">
      <PanelHead title="📊 Panel dinámico" count={filtradas.length} total={rows.length} onLimpiar={limpiar} />
      <div className="row">
        <FiltroVer value={ver} onChange={setVer} opciones={[
          ["activos", "Activos"], ["bajas", "Bajas"], ["todos", "Todos"],
        ]} />
        <button className={`lg-venc ${soloRevisar ? "on" : ""}`} onClick={() => setSoloRevisar((v) => !v)}>🚩 Solo marcados para revisión</button>
        <div className="panel-acciones">
          <button className="btn-soft" onClick={() => setPendCopia(true)} title="Pisos con menos llaves de las necesarias"
                  style={pisosCopias > 0 ? { background: "#dc2626", color: "#fff", borderColor: "#dc2626" } : undefined}>
            📦 Pisos que necesitan copias
            {pisosCopias > 0 && <span className="tab-badge" style={{ background: "#fff", color: "#dc2626" }}>{pisosCopias}</span>}
          </button>
          <button className="btn-soft" onClick={() => setHistGeneral(true)} title="Todo lo que ha pasado en todos los pisos">📜 Historial general</button>
        </div>
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <FilterHead campos={campos} filtros={filtros} setFiltro={setFiltro} extra={<><th></th><th></th><th></th></>} />
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.id} className={r.revisar ? "revisar" : ""}>
                <td className="mono">{r.pid}</td>
                <td className="strong">{r.codigo || r.pid}</td>
                <td className="num-cell">
                  {editCaj?.pid === r.pid ? (
                    <span style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                      <input style={{ width: 80 }} value={editCaj.valor} placeholder="nº cajetín"
                             onChange={(e) => setEditCaj((p) => ({ ...p, valor: e.target.value }))}
                             onKeyDown={(e) => e.key === "Enter" && guardarCajetin(r.pid, editCaj.valor)}
                             autoFocus />
                      <button className="btn btn-mini" onClick={() => guardarCajetin(r.pid, editCaj.valor)} title="Guardar">✓</button>
                      <button className="btn btn-mini" onClick={() => setEditCaj(null)} title="Cancelar">✕</button>
                    </span>
                  ) : (
                    <span style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                      {r.cajetin ?? <span className="muted">sin asignar</span>}
                      <button className="btn btn-mini" onClick={() => setEditCaj({ pid: r.pid, valor: r.cajetin || "" })}
                              title={r.cajetin ? "Cambiar cajetín" : "Asignar cajetín"}>
                        🗝️
                      </button>
                    </span>
                  )}
                </td>
                <td className="num-cell">{r.reales}</td>
                <td className="num-cell">
                  {editNec?.pid === r.pid ? (
                    <span style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                      <input className="mono" style={{ width: 50 }} value={editNec.valor}
                             placeholder={`auto ${r.necesarias_auto ?? r.plazas_totales + 6}`}
                             onChange={(e) => setEditNec((p) => ({ ...p, valor: e.target.value }))}
                             onKeyDown={(e) => e.key === "Enter" && guardarNecesarias(r.pid, editNec.valor)}
                             autoFocus />
                      <button className="btn btn-mini" onClick={() => guardarNecesarias(r.pid, editNec.valor)} title="Guardar">✓</button>
                      <button className="btn btn-mini" onClick={() => setEditNec(null)} title="Cancelar">✕</button>
                    </span>
                  ) : (
                    <span style={{ cursor: "pointer" }} title={`Clic para fijar un objetivo manual (si no, automático: plazas + ${r.margen ?? 6}${r.empresa ? ` por ser ${r.empresa}` : ""}; AT +5, no adscrito +3, resto +6)`}
                          onClick={() => setEditNec({ pid: r.pid, valor: r.necesarias_manual != null ? String(r.necesarias_manual) : "" })}>
                      {r.falta > 0 ? <Badge tone="amber">⚠ {r.necesarias}</Badge> : r.necesarias}
                      {r.necesarias_manual != null && <span title="Objetivo manual (no automático)"> ✏️</span>}
                    </span>
                  )}
                </td>
                <td className="num-cell">{r.prestadas}</td>
                <td className="num-cell">{r.en_cajetin}</td>
                <td className="num-cell">
                  {r.sin_codigo > 0 ? <Badge tone="amber">{r.sin_codigo}</Badge> : (r.sin_codigo ?? 0)}
                </td>
                <td>{r.activo ? <Badge tone="green">activo</Badge> : <Badge tone="red">baja</Badge>}</td>
                <td>
                  <button className={`btn btn-mini btn-revisar ${r.revisar ? "on" : ""}`}
                          title={r.revisar ? `Marcado para revisión: ${r.revisar_nota || "(sin motivo)"}` : "Marcar para revisión"}
                          onClick={() => setRevisarModal({ pid: r.pid, codigo: r.codigo || r.pid, valor: !r.revisar, nota: r.revisar_nota || "" })}>
                    {r.revisar ? "✅" : "🚩"}
                  </button>
                </td>
                <td>
                  <button className="btn btn-mini" onClick={() => setInfoPiso({ pid: r.pid, codigo: r.codigo || r.pid })} title="Configuración de llaves y foto de este piso">
                    ℹ️
                  </button>
                </td>
                <td>
                  <button className="btn btn-mini" onClick={() => setHistPiso({ pid: r.pid, codigo: r.codigo || r.pid })} title="Historial del piso">
                    📜
                  </button>
                </td>
              </tr>
            ))}
            {filtradas.length === 0 && <tr><td colSpan={12} className="empty">🗂️ Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />
      {revisarModal && (
        <div className="modal-back" onMouseDown={() => setRevisarModal(null)}>
          <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
            <h3>🚩 {revisarModal.valor ? "Marcar para revisión" : "Resolver revisión"} — {revisarModal.codigo}</h3>
            {revisarModal.valor && (
              <>
                <label>Motivo</label>
                <input value={revisarModal.nota} onChange={(e) => setRevisarModal((p) => ({ ...p, nota: e.target.value }))} autoFocus />
              </>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={() => setRevisarModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarRevisar}>{revisarModal.valor ? "Marcar" : "Resolver"}</button>
            </div>
          </div>
        </div>
      )}
      {infoPiso && <InfoPisoModal piso={infoPiso} onClose={() => setInfoPiso(null)} onCambiado={cargar} />}
      {histPiso && <HistorialPisoModal piso={histPiso} onClose={() => setHistPiso(null)} />}
      {histGeneral && <HistorialGeneralModal onClose={() => setHistGeneral(false)} />}
      {pendCopia && <PendientesCopiaModal onClose={() => setPendCopia(false)}
                                          onVerHistorial={(p) => setHistPiso(p)} />}
    </div>
  );
}

function variantePisoVacia() {
  return { tiene: false, mec: "fisica", tarj: false, seg: false, proveedor: "", comentario: "" };
}

// Modal "Info" de un piso desde el Panel dinámico: los TRES componentes
// posibles (portal/piso/buzón) siempre visibles, marcados en color los que
// tiene el piso — igual que la ficha de "llave nueva" (FichaLlave), pero
// aquí editable directamente y de una sola vez con un botón Guardar, sin
// tener que dar de alta ninguna llave.
function InfoPisoModal({ piso, onClose, onCambiado }) {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [comp, setComp] = useState({ portal: variantePisoVacia(), piso: variantePisoVacia(), buzon: variantePisoVacia() });
  const [usos, setUsos] = useState({});
  const { avisar } = useDialog();

  useEffect(() => {
    api.get(`/piso_defaults/${piso.pid}`).then(({ data }) => {
      const sugerencia = data.sugerencia || {};
      setComp((prev) => {
        const next = { ...prev };
        for (const tipo of ["portal", "piso", "buzon"]) {
          const s = sugerencia[tipo];
          if (s) {
            next[tipo] = { tiene: true, mec: s.mecanismo, tarj: !!s.tarjeta, seg: !!s.es_seguridad,
                          proveedor: s.proveedor || "", comentario: s.comentario || "" };
          }
        }
        return next;
      });
      const n = {};
      for (const v of data.variantes || []) n[v.tipo] = (n[v.tipo] || 0) + v.n_llaves;
      setUsos(n);
      setCargando(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piso.pid]);

  const set1 = (tipo, campo, v) => setComp((prev) => ({ ...prev, [tipo]: { ...prev[tipo], [campo]: v } }));

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.post(`/piso_defaults/${piso.pid}/guardar_variantes`, {
        portal: comp.portal.tiene ? comp.portal : null,
        piso: comp.piso.tiene ? comp.piso : null,
        buzon: comp.buzon.tiene ? comp.buzon : null,
      });
      if (data.ok) {
        onCambiado?.();
        onClose();
      } else {
        avisar(data.error || "No se pudo guardar.");
      }
    } catch (err) {
      avisar(err.response?.data?.error || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  // Se llama como función (filaComponente(...)), NO como <Fila />: si fuera un
  // componente definido aquí dentro, React lo recrearía en cada tecla y los
  // campos de texto perderían el foco tras cada letra.
  const filaComponente = (tipo, nombre) => (
    <div key={tipo} className={`componente-row t-${tipo}${comp[tipo].tiene ? " activo" : ""}`}>
      <label className="componente-row-head">
        <input type="checkbox" checked={comp[tipo].tiene} onChange={(e) => set1(tipo, "tiene", e.target.checked)} />
        <span className="cr-ico">{ICONO_COMPONENTE[tipo]}</span>
        <span className="cr-nombre">{nombre}</span>
        {usos[tipo] > 0 && <span className="fecha-copia">{usos[tipo]} llave(s) la usan ahora</span>}
      </label>
      {comp[tipo].tiene && (
        <>
          <ComponenteCampos tipo={tipo} valor={comp[tipo]} onChange={(campo, v) => set1(tipo, campo, v)} />
          {tipo !== "buzon" && comp[tipo].seg && (
            <input placeholder="Proveedor de seguridad" value={comp[tipo].proveedor}
                   onChange={(e) => set1(tipo, "proveedor", e.target.value)} style={{ marginTop: 6 }} />
          )}
          <input placeholder="Comentario" value={comp[tipo].comentario}
                 onChange={(e) => set1(tipo, "comentario", e.target.value)} style={{ marginTop: 6 }} />
        </>
      )}
    </div>
  );

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>ℹ️ Configuración de llaves — {piso.codigo}</h3>
        <p className="muted">Marca lo que tiene este piso — se usa para sugerir y explicar la ficha al dar de alta una llave nueva.</p>
        {cargando ? <p className="muted">Cargando…</p> : (
          <>
            {filaComponente("portal", "Portal")}
            {filaComponente("piso", "Piso")}
            {filaComponente("buzon", "Buzón")}
            <FotoPiso pid={piso.pid} />
          </>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={cargando || guardando}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- INVENTARIO LLAVES
// Icono compacto para un componente de detalle_llaves (portal/piso/buzon),
// usado en la columna "Componentes" del inventario — de un vistazo se ve el
// mecanismo de CADA componente por separado (antes se resumía en una sola
// columna "Mecanismo" que mezclaba varios componentes con mecanismos
// distintos en un único valor, perdiendo esa información).
// Marcar/desmarcar revisión manual de una llave: "esta llave" (todos sus
// componentes en detalle_llaves) o "todo el piso" (piso_ajustes, la misma
// bandera que usa el 🚩 del Panel dinámico).
// El propio botón 🚩 que abre esto ya decide si es marcar o resolver (según
// el estado actual de esa llave), así que aquí no hace falta un checkbox
// redundante para repetir esa misma decisión — solo elegir el ámbito y,
// si se está marcando, el motivo.
function RevisarLlaveModal({ info, onClose, onGuardado }) {
  const [nota, setNota] = useState(info.nota || "");
  const [alcance, setAlcance] = useState(info.alcance || "llave");

  const guardar = async () => {
    if (info.valor) {
      // Marcar: hay que elegir a qué se aplica.
      if (alcance === "piso") {
        await api.post("/piso_ajuste", { piso_id: info.piso_id, campo: "revisar", valor: true, nota });
      } else {
        await api.post("/llave_revisar", { llave_id: info.llave_id, valor: true, nota });
      }
    } else {
      // Resolver: da igual de qué tipo era la marca (de la llave, del piso,
      // o de las dos) — se quitan ambas, sin tener que elegir.
      await Promise.all([
        api.post("/llave_revisar", { llave_id: info.llave_id, valor: false }),
        api.post("/piso_ajuste", { piso_id: info.piso_id, campo: "revisar", valor: false }),
      ]);
    }
    onGuardado();
    onClose();
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
        <h3>{info.valor ? "🚩 Marcar para revisión" : "✅ Resolver revisión"} — llave {info.codigo ?? "s/c"} ({info.piso})</h3>
        {info.valor && (
          <>
            <div style={{ display: "flex", gap: 14, margin: "10px 0" }}>
              <label><input type="radio" name="revisar-alcance" checked={alcance === "llave"} onChange={() => setAlcance("llave")} /> Solo esta llave</label>
              <label><input type="radio" name="revisar-alcance" checked={alcance === "piso"} onChange={() => setAlcance("piso")} /> Todo el piso</label>
            </div>
            <label>Motivo</label>
            <input value={nota} onChange={(e) => setNota(e.target.value)} autoFocus />
          </>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar}>{info.valor ? "Marcar" : "Resolver"}</button>
        </div>
      </div>
    </div>
  );
}

const ICONO_TIPO = { portal: "🚪", piso: "🏠", buzon: "📮" };
function ChipComponente({ c }) {
  return (
    <Badge tone={c.es_seguridad ? "blue" : "gray"}>
      {ICONO_TIPO[c.tipo] || c.tipo} {c.mecanismo === "magnetica" ? "🧲" : "🗝️"}
      {c.tarjeta ? " 💳" : ""}{c.es_seguridad ? " 🔒" : ""}
    </Badge>
  );
}

// Botón TEMPORAL para volcar de golpe todo el inventario desde el Excel
// "Llaves 24_25" (hojas "Dinámico" y "Prestadas"), sustituyendo lo que haya
// en la app — a petición expresa del cliente, para una única migración de
// datos. Quitar este componente (y el botón de abajo) una vez usado.
function ImportadorInventarioTemporal({ onImportado }) {
  const [archivo, setArchivo] = useState(null);
  const [frase, setFrase] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const { confirmar } = useDialog();

  const listo = archivo && frase.trim().toUpperCase() === "BORRAR TODO";

  const importar = async () => {
    if (!listo) return;
    const ok = await confirmar(
      "Esto BORRA TODO el inventario actual (llaves, préstamos, cajetines e historial) y lo reconstruye " +
      "solo con lo que traiga el Excel. Se hace una copia de seguridad automática de la base de datos antes " +
      "de borrar, pero la operación en la app no se puede deshacer desde aquí.\n\n¿Continuar?"
    );
    if (!ok) return;
    setSubiendo(true);
    setError(null);
    setResultado(null);
    const fd = new FormData();
    fd.append("archivo", archivo);
    fd.append("confirmar", "1");
    try {
      const { data } = await api.post("/importar_inventario_completo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      if (data.ok) {
        setResultado(data);
        setArchivo(null);
        setFrase("");
        onImportado();
      } else {
        setError(data.error || "No se pudo importar.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Error de conexión.");
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className="warn-box" style={{ marginBottom: 12, maxWidth: 640 }}>
      <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
        ⚠️ Importar Excel completo (herramienta temporal — sustituye TODO el inventario)
      </div>
      <p className="muted" style={{ margin: "0 0 8px" }}>
        Sube el Excel "Llaves 24_25" (con las hojas "Dinámico" y "Prestadas"). Borra el inventario, préstamos,
        cajetines e historial actuales y los reconstruye solo con lo que traiga el archivo. Se hace copia de
        seguridad automática de la base de datos justo antes de borrar.
      </p>
      <input type="file" accept=".xlsx" onChange={(e) => setArchivo(e.target.files?.[0] || null)} disabled={subiendo} />
      <div style={{ marginTop: 8, maxWidth: 260 }}>
        <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#92400e", marginBottom: 4 }}>
          Escribe BORRAR TODO para habilitar el botón
        </label>
        <input value={frase} onChange={(e) => setFrase(e.target.value)} disabled={subiendo} placeholder="BORRAR TODO" />
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {resultado && (
        <div className="alert alert-ok">
          Importado: {resultado.pisos_procesados} pisos · llaves creadas — prestadas {resultado.llaves.prestadas},
          en cajetín {resultado.llaves.recibidas}, perdidas {resultado.llaves.perdidas} · {resultado.prestamos_creados} préstamos
          · {resultado.cajetines_asignados} cajetines asignados.
          {resultado.saltadas_total > 0 && <> · {resultado.saltadas_total} fila(s) saltada(s) (piso no encontrado o código repetido).</>}
          {resultado.avisos_total > 0 && <> · {resultado.avisos_total} aviso(s) (p.ej. cajetín repetido).</>}
          <div className="muted" style={{ marginTop: 4 }}>Copia de seguridad guardada en: {resultado.backup}</div>
        </div>
      )}
      {resultado?.saltadas?.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600, color: "#92400e" }}>
            Ver las {resultado.saltadas_total} fila(s) saltada(s)
          </summary>
          <div className="tablewrap" style={{ marginTop: 6, maxHeight: 260, overflowY: "auto" }}>
            <table>
              <thead><tr><th>Fila</th><th>Piso</th><th>Motivo</th></tr></thead>
              <tbody>
                {resultado.saltadas.map((s, i) => (
                  <tr key={i}><td>{s.fila}</td><td>{s.piso || "—"}</td><td>{s.motivo}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      {resultado?.avisos?.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600, color: "#92400e" }}>
            Ver los {resultado.avisos_total} aviso(s)
          </summary>
          <div className="tablewrap" style={{ marginTop: 6, maxHeight: 260, overflowY: "auto" }}>
            <table>
              <thead><tr><th>Fila</th><th>Piso</th><th>Motivo</th></tr></thead>
              <tbody>
                {resultado.avisos.map((s, i) => (
                  <tr key={i}><td>{s.fila}</td><td>{s.piso || "—"}</td><td>{s.motivo}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      <div className="modal-actions" style={{ justifyContent: "flex-start", marginTop: 10 }}>
        <button className="btn btn-danger" onClick={importar} disabled={!listo || subiendo}>
          {subiendo ? "Importando… puede tardar un minuto" : "Borrar todo e importar"}
        </button>
      </div>
    </div>
  );
}

// Da de alta un juego de llaves nuevo para un piso, sin pasar por Prestar ni
// por una ruta — primero se elige el piso, luego se rellena la misma ficha
// de siempre (reutiliza FichaLlaveModal, con los valores por defecto del
// piso si los tiene).
function CrearJuegoModal({ onClose, onCreado }) {
  const [buscaPiso, setBuscaPiso] = useState("");
  const [matches, setMatches] = useState([]);
  const navPiso = useNavegacionLista(matches, (x) => elegirPiso(x), () => setMatches([]));
  const [piso, setPiso] = useState(null); // {id_origen, codigo}
  const [defaults, setDefaults] = useState(null);
  const [creados, setCreados] = useState(null); // códigos recién creados -> ventana de resultado
  const { avisar, confirmar } = useDialog();

  const onBusca = (v) => {
    setBuscaPiso(v); setPiso(null);
    if (!v.trim()) { setMatches([]); return; }
    api.get("/buscar_piso", { params: { q: v.trim() } }).then(({ data }) => setMatches(data || [])).catch(() => setMatches([]));
  };
  const elegirPiso = async (x) => {
    setPiso({ id_origen: x.id_origen, codigo: x.codigo });
    setBuscaPiso(x.codigo);
    setMatches([]);
    try {
      const { data } = await api.get(`/piso_defaults/${x.id_origen}`);
      setDefaults({ sugerencia: data.sugerencia, variantes: data.variantes });
    } catch { setDefaults(null); }
  };

  const crearLlaves = async (campos) => {
    try {
      const { data } = await api.post("/crear_juego", { piso_id: piso.id_origen, ...campos });
      if (data.ok) {
        setCreados(data.codigos || [data.codigo_completo]);
      } else {
        await avisar(data.error || "No se pudo crear.");
      }
    } catch (err) {
      await avisar(err.response?.data?.error || "No se pudo crear.");
    }
  };

  // Resultado: todos los códigos creados. Al cerrar se avisa de que no se
  // volverán a ver aquí (se pueden consultar en Inventario / historial).
  if (creados) {
    const cerrarResultado = async () => {
      const ok = await confirmar(
        `Se cerrará esta ventana con ${creados.length === 1 ? "el código nuevo" : `los ${creados.length} códigos nuevos`}.\n\n` +
        "¿Seguro que ya has apuntado o etiquetado " + (creados.length === 1 ? "el código?" : "todos los códigos?"));
      if (ok) onCreado();
    };
    const copiar = async () => {
      try { await navigator.clipboard.writeText(creados.join("\n")); avisar("Códigos copiados al portapapeles."); }
      catch { avisar("No se pudieron copiar: apúntalos a mano."); }
    };
    return (
      <div className="modal-back" onMouseDown={cerrarResultado}>
        <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
          <h3>✅ {creados.length === 1 ? "Llave creada" : `${creados.length} llaves creadas`}</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            {piso.codigo} · apunta o etiqueta {creados.length === 1 ? "este código" : "estos códigos"} en las llaves físicas:
          </p>
          <div className="codigos-creados">
            {creados.map((cod) => <span key={cod} className="codigo-creado">{cod}</span>)}
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={copiar}>📋 Copiar códigos</button>
            <button className="btn btn-primary" onClick={cerrarResultado} autoFocus>OK</button>
          </div>
        </div>
      </div>
    );
  }

  if (!piso) {
    return (
      <div className="modal-back" onMouseDown={onClose}>
        <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
          <h3>🔑 Crear juego nuevo</h3>
          <p className="muted">Elige el piso para el que quieres dar de alta una llave nueva.</p>
          <div style={{ position: "relative" }}>
            <input value={buscaPiso} placeholder="Buscar piso..." onChange={(e) => onBusca(e.target.value)}
                   onKeyDown={navPiso.onKeyDown} autoComplete="off" autoFocus />
            {matches.length > 0 && (
              <div className="deplist" ref={navPiso.listaRef}>
                {matches.map((x, i) => (
                  <div {...navPiso.opcion(i)} key={x.id_origen} onMouseDown={(e) => { e.preventDefault(); elegirPiso(x); }}>
                    {x.codigo} <span className="muted">· ID {x.id_origen}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={onClose}>Cancelar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FichaLlaveModal
      piso={{ id: piso.id_origen, codigo: piso.codigo }}
      defaults={defaults}
      peligroso
      conUnidades
      onCancel={onClose}
      onConfirmar={crearLlaves}
    />
  );
}

// Situación de una llave en Inventario -> [texto, tono del Badge].
const SITUACION_LLAVE = {
  cajetin: ["En cajetín", "green"],
  prestada: ["Prestada", "amber"],
  preparada: ["Preparada", "blue"],
  perdida: ["Perdida", "red"],
};

// Marcar una llave como perdida (baja). Pide motivo; avisa fuerte si está
// prestada (se da por perdida en manos de esa persona) y no deja si está
// preparada sin entregar (el backend también lo impide).
function MarcarPerdidaModal({ llave, onClose, onHecho }) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const etiqueta = `${llave.pid}-${llave.codigo ?? "?"}`;

  const guardar = async () => {
    setError(null);
    if (!motivo.trim()) { setError("Indica el motivo."); return; }
    setGuardando(true);
    try {
      const { data } = await api.post("/baja_juego", { llave_id: llave.llave_id, motivo: motivo.trim() });
      if (data.ok) onHecho(); else setError(data.error || "No se pudo dar de baja.");
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo dar de baja.");
    } finally { setGuardando(false); }
  };

  return (
    <div className="modal-back modal-back-peligro" onMouseDown={onClose}>
      <div className="modal modal-sm modal-peligro" onMouseDown={(e) => e.stopPropagation()}>
        <h3>⛔ Marcar como perdida</h3>
        <p>Llave <b>{etiqueta}</b> · {llave.piso}{llave.cajetin ? ` · cajetín ${llave.cajetin}` : ""}</p>
        {llave.situacion === "prestada" && (
          <div className="alert alert-error">
            Está <b>prestada</b> a <b>{llave.quien || "alguien"}</b>. Se dará por perdida en su poder y quedará
            anotado en el historial de ese préstamo.
          </div>
        )}
        {llave.situacion === "preparada" && (
          <div className="alert alert-error">
            Está <b>preparada</b> para {llave.quien || "alguien"} sin entregar. Entrégala o quita esa preparación antes.
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
        <label>Motivo *</label>
        <textarea rows={2} value={motivo} maxLength={200} autoFocus
                  placeholder="Ej: no aparece en el cajetín, la perdió el inquilino…"
                  onChange={(e) => setMotivo(e.target.value)} />
        <p className="muted" style={{ fontSize: 12 }}>
          Dejará de contar en el inventario del piso. Se puede deshacer con ↩️ desde "Perdidas/baja".
        </p>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={guardar} disabled={guardando || llave.situacion === "preparada"}>
            {guardando ? "Guardando…" : "Marcar como perdida"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modo "Asignar SOLO códigos" de Contar cajetines: recorre, desde el cajetín
// elegido en adelante, solo los cajetines que tienen llaves SIN número
// (físicamente en el cajetín). No se cuenta nada: se ven todas las llaves del
// cajetín y se escribe el número de las que no lo tienen. No toca la ronda.
function AsignarSoloCodigos({ desde, onSalir }) {
  const [inicio, setInicio] = useState(String(desde ?? ""));
  const [lista, setLista] = useState(null);       // [{cajetin,pid,codigo,en_cajetin,sin_codigo}]
  const [idx, setIdx] = useState(0);
  const [det, setDet] = useState(null);           // /conteo_actual del cajetín actual
  const [usados, setUsados] = useState(new Set());
  const [valores, setValores] = useState({});     // llave_id -> número escrito
  const [errores, setErrores] = useState({});     // llave_id -> {tipo, texto}
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [quitar, setQuitar] = useState(() => new Set());   // llave_id que NO están en el cajetín
  const [extras, setExtras] = useState([]);                // códigos de llaves a añadir
  const [extraTxt, setExtraTxt] = useState("");
  const [extraAviso, setExtraAviso] = useState(null);
  const primerInput = useRef(null);
  const { avisar, confirmar, confirmarPeligro, alertaRevision } = useDialog();

  // conservar=true (p.ej. tras registrar una devolución): se mantienen los
  // números escritos, las llaves a añadir y las marcadas para quitar.
  const cargarDet = async (caj, conservar = false) => {
    if (!conservar) { setDet(null); setQuitar(new Set()); setExtras([]); setExtraTxt(""); setExtraAviso(null); setValores({}); setErrores({}); }
    try {
      const { data } = await api.get("/conteo_actual", { params: { cajetin: caj } });
      setDet(data);
      const u = await api.get(`/piso/${data.pid}/numeros_usados`);
      setUsados(new Set(u.data.usados || []));
      setTimeout(() => primerInput.current?.focus(), 0);
    } catch { setDet({ error: true }); }
  };
  const cargarLista = async (desdeCaj) => {
    setLista(null); setMsg(null);
    try {
      const { data } = await api.get("/conteo_sin_codigo", { params: desdeCaj ? { desde: desdeCaj } : {} });
      const rows = data.rows || [];
      setLista(rows); setIdx(0);
      if (rows.length) cargarDet(rows[0].cajetin); else setDet(null);
    } catch { setLista([]); }
  };
  // Solo al entrar en el modo: empieza desde el cajetín en el que se estaba.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargarLista(desde); }, []);

  const llaves = det?.llaves || [];
  const sinCod = llaves.filter((l) => l.codigo == null && !quitar.has(l.llave_id));
  const escritos = sinCod.filter((l) => (valores[l.llave_id] || "") !== "");
  const ocupados = new Set([...usados, ...escritos.map((l) => Number(valores[l.llave_id])), ...extras.map(Number)]);
  const siguienteLibre = (() => { let n = 1; while (ocupados.has(n)) n++; return n; })();

  // Duplicados al momento (sin esperar al servidor): ya usado en el piso,
  // repetido entre las llaves de aquí o igual que una llave a añadir.
  const errorLocal = (id, v, vals = valores) => {
    if (!v) return null;
    if (usados.has(Number(v))) return "Ya existe en este piso";
    if (extras.includes(String(Number(v)))) return "Igual que una llave a añadir";
    if (sinCod.some((l) => l.llave_id !== id && (vals[l.llave_id] || "") !== "" && Number(vals[l.llave_id]) === Number(v))) return "Repetido";
    return null;
  };
  const hayErroresLocales = escritos.some((l) => errorLocal(l.llave_id, valores[l.llave_id]));
  const setVal = (id, v) => {
    const nuevos = { ...valores, [id]: v };
    setValores(nuevos);
    setErrores(Object.fromEntries(sinCod.map((l) => {
      const e = errorLocal(l.llave_id, nuevos[l.llave_id] || "", nuevos);
      return [l.llave_id, e ? { tipo: "error", texto: e } : (l.llave_id === id ? null : errores[l.llave_id] || null)];
    })));
  };
  const toggleQuitar = (id) => setQuitar((q) => { const n = new Set(q); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Añadir una llave que está en el cajetín pero no en la app.
  const anadirExtra = async () => {
    const cod = extraTxt.trim();
    setExtraAviso(null);
    if (!/^\d+$/.test(cod)) { setExtraAviso({ tipo: "error", texto: "El código debe ser un número." }); return; }
    const n = String(Number(cod));
    if (llaves.some((l) => l.codigo != null && Number(l.codigo) === Number(n))) { setExtraAviso({ tipo: "error", texto: `La ${det.pid}-${n} ya está en este cajetín.` }); return; }
    if (extras.includes(n) || escritos.some((l) => Number(valores[l.llave_id]) === Number(n))) { setExtraAviso({ tipo: "error", texto: `El ${n} ya lo has puesto en este cajetín.` }); return; }
    try {
      const { data } = await api.post("/conteo_previsualizar_ajuste", { piso_id: det.piso_id, tipo: "alta", codigos: [n] });
      const f = (data.filas || [])[0] || {};
      if (!f.ya_existe) { setExtras((x) => [...x, n]); setExtraTxt(""); return; }
      if (f.situacion === "prestada") setExtraAviso({ tipo: "prestada", cod: n, quien: f.quien });
      else if (f.situacion === "perdida") setExtraAviso({ tipo: "error", texto: `La ${det.pid}-${n} está dada de baja (perdida): reactívala en Inventario (↩️).` });
      else if (f.situacion === "preparada") setExtraAviso({ tipo: "error", texto: `La ${det.pid}-${n} está preparada para ${f.quien || "alguien"}.` });
      else setExtraAviso({ tipo: "error", texto: `La ${det.pid}-${n} ya existe en este piso.` });
    } catch { setExtraAviso({ tipo: "error", texto: "No se pudo comprobar el código." }); }
  };
  // Devolver desde una llave sin número: la prestada con ese número, o una
  // prestada SIN número (mov_id) a la que se le pone.
  const devolverDesdeLlave = async (id, { cod, mov_id, quien }) => {
    if (!(await confirmar(`¿Registrar la devolución de la llave ${det.pid}-${cod} (la tenía ${quien || "alguien"})?`
      + (mov_id ? `\n\nEsa llave no tenía número en la app: se le pondrá el ${cod}.` : "")
      + "\n\nEsta llave sin número se quedará sin número: si no hay otra en el cajetín, márcala como \"No está\"."))) return;
    try {
      const r = await devolverConAvisoRevision(alertaRevision, det.pid, cod, mov_id ? { mov_id } : {});
      if (r.cancelado) return;
      if (!r.ok) { setErrores((m) => ({ ...m, [id]: { tipo: "error", texto: r.error } })); return; }
      setValores((v) => ({ ...v, [id]: "" }));
      setErrores((m) => ({ ...m, [id]: null }));
      setExtraAviso({ tipo: "ok", texto: `✓ Devuelta la ${det.pid}-${cod}: ya está en el cajetín (sale arriba con su código).` });
      cargarDet(det.cajetin, true);
    } catch (err) { setErrores((m) => ({ ...m, [id]: { tipo: "error", texto: err.response?.data?.error || "No se pudo devolver." } })); }
  };
  const devolverExtra = async () => {
    const { cod, quien } = extraAviso;
    if (!(await confirmar(`¿Registrar la devolución de la llave ${det.pid}-${cod} (la tenía ${quien || "alguien"})?`))) return;
    try {
      const r = await devolverConAvisoRevision(alertaRevision, det.pid, cod);
      if (r.cancelado) return;
      if (!r.ok) { setExtraAviso({ tipo: "error", texto: r.error }); return; }
      setExtraAviso({ tipo: "ok", texto: `✓ Devuelta la ${det.pid}-${cod}: ya está en el cajetín (sale arriba con su código).` }); setExtraTxt("");
      cargarDet(det.cajetin, true);
    } catch (err) { setExtraAviso({ tipo: "error", texto: err.response?.data?.error || "No se pudo registrar la devolución." }); }
  };

  // Comprueba el número (al salir del campo). Guarda en errores[id] también
  // la info para ofrecer devoluciones: existe (prestada con ese número) o
  // sinNumero (prestadas del piso sin número).
  const validar = async (id) => {
    const v = valores[id] || "";
    if (v === "") return true;
    const eLocal = errorLocal(id, v);
    if (eLocal && !usados.has(Number(v))) { setErrores((m) => ({ ...m, [id]: { tipo: "error", texto: eLocal } })); return false; }
    try {
      const { data } = await api.get(`/llave/${id}/validar_numero`, { params: { numero: v } });
      if (!data.ok) {
        const ex = data.existe;
        const texto = ex?.situacion === "prestada" ? `Ya existe: prestada a ${ex.quien || "alguien"}`
          : ex?.situacion === "perdida" ? "Ya existe, dada de baja (perdida)"
          : ex?.situacion === "preparada" ? `Ya existe: preparada para ${ex.quien || "alguien"}` : data.error;
        setErrores((m) => ({ ...m, [id]: { tipo: "error", texto, existe: ex, valor: v } }));
        return false;
      }
      if (eLocal) { setErrores((m) => ({ ...m, [id]: { tipo: "error", texto: eLocal } })); return false; }
      setErrores((m) => ({ ...m, [id]: { tipo: data.aviso ? "aviso" : "info", texto: data.aviso ? "Existe en otro piso" : "",
                                         sinNumero: data.prestadas_sin_numero || [], valor: v } }));
      return true;
    } catch { return false; }
  };

  const hayCambios = escritos.length > 0 || extras.length > 0 || quitar.size > 0;
  const guardar = async () => {
    if (!hayCambios || guardando || hayErroresLocales) return;
    const quitarL = llaves.filter((l) => quitar.has(l.llave_id));
    const etq = (l) => (l.codigo != null ? `${det.pid}-${l.codigo}` : "sin número");
    if (quitarL.length && !(await confirmarPeligro(
      `Vas a dar de BAJA (perdidas) ${quitarL.length} llave(s) que no están en el cajetín ${det.cajetin}:\n` +
      quitarL.map((l) => `· ${etq(l)}`).join("\n"), { titulo: "Quitar llaves del cajetín", boton: "Sí, dar de baja" }))) return;
    if (extras.length && !(await confirmar(`Se darán de ALTA ${extras.length} llave(s): ${extras.map((c) => `${det.pid}-${c}`).join(", ")}. ¿Continuar?`))) return;
    setGuardando(true);
    try {
      for (const l of escritos) { if (!(await validar(l.llave_id))) { setGuardando(false); return; } }
      let asignadas = 0;
      if (escritos.length) {
        const { data } = await api.post("/llaves/asignar_numeros", {
          asignaciones: escritos.map((l) => ({ llave_id: l.llave_id, numero: valores[l.llave_id] })),
        });
        if (!data.ok) { avisar(data.error || "No se pudo guardar."); return; }
        asignadas = data.asignadas;
      }
      if (extras.length || quitarL.length) {
        const { data } = await api.post("/conteo_cuadrar", {
          piso_id: det.piso_id, cajetin: det.cajetin, origen: "codigos",
          presentes: llaves.filter((l) => !quitar.has(l.llave_id)).map((l) => l.llave_id),
          bajas: quitarL.map((l) => l.llave_id), altas: extras,
          confirmacion: quitarL.length ? "eliminar" : undefined,
        });
        if (!data.ok) { avisar(data.error || "No se pudo aplicar."); cargarDet(det.cajetin, true); return; }
      }
      const partes = [asignadas && `${asignadas} código(s)`, extras.length && `${extras.length} añadida(s)`, quitarL.length && `${quitarL.length} quitada(s)`].filter(Boolean).join(" · ");
      const quedan = sinCod.length - escritos.length;
      if (quedan === 0) {
        setMsg({ tipo: "ok", texto: `✓ Cajetín ${det.cajetin}: ${partes}. Ya no le falta ningún código.` });
        irA(idx + 1, true);
      } else {
        setMsg({ tipo: "ok", texto: `✓ ${partes}. Quedan ${quedan} sin código en este cajetín.` });
        cargarDet(det.cajetin);
      }
    } catch (err) { avisar(err.response?.data?.error || "No se pudo guardar."); }
    finally { setGuardando(false); }
  };

  // Cambiar de cajetín: si quedan llaves sin código (o números sin guardar), aviso en rojo.
  const irA = async (i, sinPreguntar = false) => {
    if (!lista) return;
    if (i >= lista.length) { setDet(null); setIdx(lista.length); return; }
    if (i < 0) return;
    const pendientes = sinCod.length;
    const sinGuardar = escritos.length + extras.length + quitar.size;
    if (!sinPreguntar && (pendientes > 0 || sinGuardar > 0) && !(await confirmarPeligro(
      (pendientes ? `En el cajetín ${det?.cajetin} quedan ${pendientes} llave(s) SIN código` : `En el cajetín ${det?.cajetin} hay cambios`) +
      (sinGuardar ? ` (y ${sinGuardar} cambio(s) sin guardar, que se perderán)` : "") +
      ".\n\n¿Pasar a otro cajetín igualmente?",
      { titulo: "Cajetín sin terminar", boton: "Sí, pasar" }))) return;
    setIdx(i); cargarDet(lista[i].cajetin);
  };

  const fin = lista && idx >= lista.length;
  return (
    <div className="solo-codigos">
      <div className="sc-barra">
        <b>🔢 Modo ASIGNAR SOLO CÓDIGOS</b>
        {lista && !fin && <span className="sc-prog">cajetín {idx + 1} de {lista.length} con llaves sin código</span>}
        <span style={{ flex: 1 }} />
        <span className="sc-desde">
          Desde el cajetín
          <input value={inicio} onChange={(e) => setInicio(e.target.value)} onKeyDown={(e) => e.key === "Enter" && cargarLista(inicio.trim())} />
          <button className="btn btn-mini" onClick={() => cargarLista(inicio.trim())}>Ir</button>
        </span>
      </div>
      {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}
      {!lista ? <p className="muted">Buscando cajetines con llaves sin código…</p>
        : lista.length === 0 ? <div className="alert alert-ok">🎉 No hay ningún cajetín con llaves sin código desde el {inicio || "principio"}.</div>
        : fin ? (
          <div className="alert alert-ok" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            🎉 Has llegado al final de la lista ({lista.length} cajetines).
            <button className="btn btn-mini" onClick={() => irA(lista.length - 1, true)}>‹ Volver al último</button>
            <button className="btn btn-mini" onClick={onSalir}>Salir del modo</button>
          </div>
        ) : !det ? <p className="muted">Cargando cajetín…</p> : det.error ? <div className="alert alert-error">No se pudo cargar el cajetín.</div> : (
          <>
            <div className="cc-cabecera">
              <div className="cajbox-big"><span className="lbl">Cajetín</span><span className="val">{det.cajetin}</span></div>
              <div className="cc-espera sc">
                <span className="ce-lbl">Hay</span><span className="ce-num">{llaves.length}</span>
                <span className="ce-lbl">llave{llaves.length === 1 ? "" : "s"} en el cajetín · faltan</span>
                <span className="ce-num falta">{sinCod.length}</span><span className="ce-lbl">con código</span>
              </div>
            </div>
            <div className="cc-piso">
              <span className="cp-lbl">🏠 Piso</span><span className="cp-nombre">{det.codigo}</span><span className="cp-id">ID {det.pid}</span>
            </div>
            <div className="ac-libre" style={{ marginBottom: 10 }}>Siguiente número libre del piso: <b>{siguienteLibre}</b></div>
            <div className="cc-tiles">
              {llaves.map((l) => quitar.has(l.llave_id) ? (
                <div key={l.llave_id} className="cc-tile falta">
                  <span className="cc-tile-ico">✗</span><span className="cc-tile-cod">{l.codigo != null ? l.codigo : "sin nº"}</span><small>se quitará (baja)</small>
                  <button type="button" className="sc-quitar on" onClick={() => toggleQuitar(l.llave_id)}>↩️ Sí está</button>
                </div>
              ) : l.codigo != null ? (
                <div key={l.llave_id} className="cc-tile pend sc-con">
                  <span className="cc-tile-ico">🔑</span><span className="cc-tile-cod">{l.codigo}</span><small>ya tiene código</small>
                  <button type="button" className="sc-quitar" onClick={() => toggleQuitar(l.llave_id)}>✕ No está</button>
                </div>
              ) : (
                <div key={l.llave_id} className={`cc-tile sc-sin ${(valores[l.llave_id] || "") !== "" ? "escrito" : ""} ${errores[l.llave_id]?.tipo === "error" ? "con-error" : ""}`}>
                  <span className="cc-tile-ico">?</span>
                  <input ref={sinCod[0]?.llave_id === l.llave_id ? primerInput : undefined} className="mono sc-input" inputMode="numeric"
                         value={valores[l.llave_id] || ""} placeholder="nº"
                         onChange={(e) => setVal(l.llave_id, e.target.value.replace(/\D/g, ""))}
                         onBlur={() => validar(l.llave_id)}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const sig = e.currentTarget.closest(".cc-tiles").querySelectorAll("input")[[...e.currentTarget.closest(".cc-tiles").querySelectorAll("input")].indexOf(e.currentTarget) + 1]; if (sig) sig.focus(); else guardar(); } }} />
                  {(valores[l.llave_id] || "") === "" && (
                    <button type="button" className="cc-asignar" onClick={() => setVal(l.llave_id, String(siguienteLibre))}>usar {siguienteLibre}</button>
                  )}
                  {errores[l.llave_id]?.texto && <small className={errores[l.llave_id].tipo === "error" ? "sc-err" : ""}>{errores[l.llave_id].texto}</small>}
                  {errores[l.llave_id] && (
                    <OpcionesDevolverNumero compacto pid={det.pid} numero={valores[l.llave_id] || ""} existe={errores[l.llave_id].existe}
                                            sinNumero={errores[l.llave_id].valor === (valores[l.llave_id] || "") ? errores[l.llave_id].sinNumero : null}
                                            onDevolver={(x) => devolverDesdeLlave(l.llave_id, x)} />
                  )}
                  <button type="button" className="sc-quitar" onClick={() => toggleQuitar(l.llave_id)}>✕ No está</button>
                </div>
              ))}
              {extras.map((cod) => (
                <div key={`x-${cod}`} className="cc-tile nueva">
                  <span className="cc-tile-ico">✓</span><span className="cc-tile-cod">{cod}</span><small>añadir</small>
                  <button type="button" className="cc-tile-x" title="Quitar" onClick={() => setExtras((x) => x.filter((c) => c !== cod))}>✕</button>
                </div>
              ))}
            </div>
            <div className="cc-extra" style={{ marginTop: 12 }}>
              <label>＋ Añadir una llave que está en el cajetín y no sale aquí</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="mono" inputMode="numeric" value={extraTxt} placeholder="Código de la llave a añadir"
                       onChange={(e) => { setExtraTxt(e.target.value.replace(/\D/g, "")); setExtraAviso(null); }}
                       onKeyDown={(e) => { if (e.key === "Enter" && extraTxt.trim()) { e.preventDefault(); anadirExtra(); } }} />
                <button className="btn" onClick={anadirExtra} disabled={!extraTxt.trim()}>＋ Añadir</button>
              </div>
              {extraAviso?.tipo === "prestada" && (
                <div className="alert alert-error" style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span>La <b>{det.pid}-{extraAviso.cod}</b> consta <b>prestada a {extraAviso.quien || "alguien"}</b>. Si está en el cajetín, es que la han devuelto.</span>
                  <button className="btn btn-mini btn-primary" onClick={devolverExtra}>↩️ Registrar devolución</button>
                </div>
              )}
              {extraAviso && extraAviso.tipo !== "prestada" && (
                <div className={`alert ${extraAviso.tipo === "ok" ? "alert-ok" : "alert-error"}`} style={{ marginTop: 8 }}>{extraAviso.texto}</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <button className="btn-registrar sc-guardar" onClick={guardar} disabled={!hayCambios || guardando || hayErroresLocales}>
                {guardando ? "Guardando…"
                  : hayErroresLocales ? "Corrige los números en rojo"
                  : `Guardar cambios${[escritos.length && ` · ${escritos.length} código(s)`, extras.length && ` · +${extras.length}`, quitar.size && ` · −${quitar.size}`].filter(Boolean).join("")}`}
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              <button className="btn" onClick={() => irA(idx - 1)} disabled={idx === 0}>‹ Cajetín anterior</button>
              <button className="btn" onClick={() => irA(idx + 1)}>Siguiente cajetín ›</button>
              <span style={{ flex: 1 }} />
              <button className="btn" onClick={onSalir}>Salir del modo</button>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>Intro: pasar a la siguiente llave; en la última, guarda.</p>
          </>
        )}
    </div>
  );
}

// Opciones de devolución al escribir un número en Asignar códigos / Asignar
// número: (1) el número ya es de una llave de este piso que consta PRESTADA
// -> devolverla; (2) el piso tiene llaves prestadas SIN número -> si la que
// tienes en la mano es una de ellas, devolverla con ese número (se le pone).
// onDevolver({cod, mov_id?, quien}) hace la devolución.
function OpcionesDevolverNumero({ pid, numero, existe, sinNumero, onDevolver, compacto = false }) {
  if (existe?.situacion === "prestada") {
    return (
      <div className={`odn odn-prestada ${compacto ? "compacto" : ""}`}>
        {!compacto && <span>La <b>{pid}-{existe.codigo}</b> consta <b>prestada a {existe.quien || "alguien"}</b>. Si es la que tienes en la mano, es que la han devuelto.</span>}
        <button type="button" className="btn btn-mini btn-primary" onClick={() => onDevolver({ cod: String(existe.codigo), quien: existe.quien })}>
          ↩️ Devolver {pid}-{existe.codigo}{compacto ? ` (${existe.quien || "prestada"})` : ""}
        </button>
      </div>
    );
  }
  if (numero !== "" && numero != null && !existe && sinNumero?.length) {
    return (
      <div className={`odn odn-sin-numero ${compacto ? "compacto" : ""}`}>
        <span>⚠️ Este piso tiene <b>{sinNumero.length} llave(s) prestada(s) SIN número</b>. Si la que tienes en la mano es una de ellas, no se lo pongas a esta: devuélvela con el {numero}.</span>
        <div className="odn-botones">
          {sinNumero.map((p) => (
            <button key={p.mov_id} type="button" className="btn btn-mini btn-primary"
                    onClick={() => onDevolver({ cod: String(numero), mov_id: p.mov_id, quien: p.quien })}>
              ↩️ Era la de {p.quien || "—"}{p.desde ? ` (desde ${isoToEs(p.desde)})` : ""}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// Asistente "Asignar códigos" (Contar cajetines): recorre de una en una las
// llaves SIN número del cajetín. El campo empieza vacío; arriba se ve el
// siguiente número libre del piso (contando los que ya se han elegido aquí).
// Nada se guarda hasta pulsar "Guardar" al final, así que se puede volver
// atrás y cambiar cualquier número sin confirmaciones por el camino.
// También se puede: marcar una llave como "no está" (queda tachada en el
// conteo y se da de baja al registrar el cajetín), añadir llaves que están en
// el cajetín y no salen (se añaden al conteo) y, si la que se añade consta
// prestada, registrar su devolución (esto sí es inmediato, como en el conteo).
function AsignarCodigosModal({ pid, pisoId, piso, llaves, extrasPadre = [], onClose, onHecho, onDevuelta }) {
  const [usados, setUsados] = useState(null);
  const [valores, setValores] = useState(() => llaves.map(() => ""));
  const [avisos, setAvisos] = useState(() => llaves.map(() => null)); // {tipo:'error'|'aviso', texto}
  const [comprobados, setComprobados] = useState(() => llaves.map(() => false));
  const [idx, setIdx] = useState(0);
  const [comprobando, setComprobando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [quitados, setQuitados] = useState(() => new Set()); // índices de llaves que NO están
  const [nuevas, setNuevas] = useState([]);                   // códigos a añadir al conteo
  const [extraTxt, setExtraTxt] = useState("");
  const [extraAviso, setExtraAviso] = useState(null);         // {tipo:'error'|'ok'|'prestada', ...}
  const [devueltas, setDevueltas] = useState([]);             // códigos devueltos desde aquí
  const inputRef = useRef(null);
  const { avisar, confirmar, alertaRevision } = useDialog();

  useEffect(() => {
    api.get(`/piso/${pid}/numeros_usados`).then(({ data }) => setUsados(new Set(data.usados || []))).catch(() => setUsados(new Set()));
  }, [pid]);
  useEffect(() => { inputRef.current?.focus(); }, [idx, usados]);
  // Al escribir un número se comprueba solo (sin esperar a Intro): por si ya
  // es de una llave prestada, o el piso tiene prestadas sin número.
  const valorActual = valores[idx];
  useEffect(() => {
    if (!valorActual || !usados) return undefined;
    const t = setTimeout(() => comprobar(idx, true), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valorActual, idx, usados]);

  const elegidos = (excepto) => new Set(valores.filter((v, i) => v !== "" && i !== excepto).map(Number));
  const aAnadir = [...extrasPadre, ...nuevas].map(Number);
  const siguienteLibre = (() => {
    if (!usados) return null;
    const ocup = new Set([...usados, ...elegidos(idx), ...aAnadir]);
    let n = 1; while (ocup.has(n)) n++;   // se propone desde el 1; el 0 se puede poner, pero a mano
    return n;
  })();
  // Comprobación inmediata (sin servidor): número ya usado en el piso o repetido aquí.
  const errorLocal = (i, v, vals = valores) => {
    if (v === "" || !usados) return null;
    if (usados.has(Number(v))) return `Ya hay una llave con el ${v} en este piso.`;
    if (aAnadir.includes(Number(v))) return `El ${v} es una llave que vas a añadir al cajetín.`;
    if (vals.some((x, j) => j !== i && x !== "" && Number(x) === Number(v))) return `El ${v} ya lo has puesto a otra llave de esta lista.`;
    return null;
  };
  const setVal = (i, v) => {
    const nuevos = valores.map((x, j) => (j === i ? v : x));
    setValores(nuevos);
    // Recalcula los errores locales de todas (al cambiar una, otra puede dejar de estar repetida).
    setAvisos((a) => a.map((x, j) => {
      const e = errorLocal(j, nuevos[j], nuevos);
      if (e) return { tipo: "error", texto: e };
      return j === i || x?.tipo === "error" ? null : x;
    }));
    setComprobados((a) => a.map((x, j) => (j === i ? false : x)));
  };
  const ponAviso = (i, av) => setAvisos((a) => a.map((x, j) => (j === i ? av : x)));

  // Comprueba el número de la llave i (sin guardar). Devuelve true si vale.
  // aviso: {tipo, texto, existe?, sinNumero?, valor}. Si el piso tiene llaves
  // prestadas sin número, la primera vez se para para que se vea la opción de
  // devolverla; con Intro otra vez se sigue.
  const comprobar = async (i, soloMirar = false) => {
    const v = valores[i];
    if (v === "") return true;
    const eLocal = errorLocal(i, v);
    if (eLocal && !usados?.has(Number(v))) { ponAviso(i, { tipo: "error", texto: eLocal }); return false; }
    try {
      const { data } = await api.get(`/llave/${llaves[i].llave_id}/validar_numero`, { params: { numero: v } });
      if (!data.ok) { ponAviso(i, { tipo: "error", texto: data.error, existe: data.existe, valor: v }); return false; }
      if (eLocal) { ponAviso(i, { tipo: "error", texto: eLocal }); return false; }
      const sinNumero = data.prestadas_sin_numero || [];
      const yaVisto = avisos[i]?.sinNumero?.length && avisos[i]?.valor === v;
      ponAviso(i, { tipo: data.aviso ? "aviso" : "info", texto: data.aviso || "", sinNumero, valor: v });
      setComprobados((a) => a.map((x, j) => (j === i ? true : x)));
      if (soloMirar) return true;
      return !(sinNumero.length && !yaVisto);
    } catch { ponAviso(i, { tipo: "error", texto: "No se pudo comprobar el número." }); return false; }
  };

  // Comprueba la actual y pasa a la siguiente (vacío = saltar esta llave).
  const siguiente = async () => {
    setComprobando(true);
    const ok = await comprobar(idx);
    setComprobando(false);
    if (ok) setIdx((i) => Math.min(i + 1, llaves.length));
  };

  // "No está": se vacía su número y queda tachada (se aplica al registrar el cajetín).
  const toggleQuitado = (i) => {
    setQuitados((q) => { const n = new Set(q); if (n.has(i)) n.delete(i); else n.add(i); return n; });
    if (!quitados.has(i)) { setVal(i, ""); ponAviso(i, null); }
  };

  // Añadir una llave que está en el cajetín y no sale (misma lógica que el conteo).
  const anadirExtra = async () => {
    const cod = String(Number(extraTxt.trim()));
    setExtraAviso(null);
    if (!/^\d+$/.test(extraTxt.trim())) { setExtraAviso({ tipo: "error", texto: "El código debe ser un número." }); return; }
    if (aAnadir.includes(Number(cod))) { setExtraAviso({ tipo: "error", texto: `La ${pid}-${cod} ya está para añadir.` }); return; }
    if (valores.some((v) => v !== "" && Number(v) === Number(cod))) { setExtraAviso({ tipo: "error", texto: `El ${cod} ya lo has puesto a una llave de esta lista.` }); return; }
    try {
      const { data } = await api.post("/conteo_previsualizar_ajuste", { piso_id: pisoId ?? pid, tipo: "alta", codigos: [cod] });
      const f = (data.filas || [])[0] || {};
      if (!f.ya_existe) { setNuevas((x) => [...x, cod]); setExtraTxt(""); setExtraAviso({ tipo: "ok", texto: `✓ La ${pid}-${cod} se añadirá al cajetín.` }); return; }
      if (f.situacion === "prestada") setExtraAviso({ tipo: "prestada", cod, quien: f.quien });
      else if (f.situacion === "preparada") setExtraAviso({ tipo: "error", texto: `La ${pid}-${cod} está PREPARADA para ${f.quien || "alguien"}: cancela esa preparación en Gestionar si ya no se va a entregar.` });
      else if (f.situacion === "perdida") setExtraAviso({ tipo: "error", texto: `La ${pid}-${cod} está dada de baja (perdida): si ha aparecido, reactívala en Inventario (↩️).` });
      else setExtraAviso({ tipo: "error", texto: `La ${pid}-${cod} ya está en este cajetín (marcada en la lista del conteo).` });
    } catch (err) { setExtraAviso({ tipo: "error", texto: err.response?.data?.error || "No se pudo comprobar el código." }); }
  };
  // Devolver desde la llave i: o la prestada que ya tiene ese número, o una
  // prestada SIN número (mov_id) a la que se le pone. La llave i (sin número)
  // del cajetín se queda sin número: no era esa.
  const devolverDesdeLlave = async (i, { cod, mov_id, quien }) => {
    if (!(await confirmar(`¿Registrar la devolución de la llave ${pid}-${cod} (la tenía ${quien || "alguien"})?`
      + (mov_id ? `\n\nEsa llave no tenía número en la app: se le pondrá el ${cod}.` : "")))) return;
    try {
      const r = await devolverConAvisoRevision(alertaRevision, pid, cod, mov_id ? { mov_id } : {});
      if (r.cancelado) return;
      if (!r.ok) { ponAviso(i, { tipo: "error", texto: r.error }); return; }
      setDevueltas((d) => [...d, cod]);
      setUsados((u) => new Set([...(u || []), Number(cod)]));
      setValores((vs) => vs.map((x, j) => (j === i ? "" : x)));
      ponAviso(i, { tipo: "ok", texto: `✓ Devuelta la ${pid}-${cod}: ya está en el cajetín, marcada como que está. `
        + "Esta llave sin número NO era esa: si en el cajetín no hay otra sin número, márcala como \"no está\"." });
      onDevuelta?.(cod);
    } catch (err) { ponAviso(i, { tipo: "error", texto: err.response?.data?.error || "No se pudo registrar la devolución." }); }
  };
  const devolverExtra = async () => {
    const { cod, quien } = extraAviso;
    if (!(await confirmar(`¿Registrar la devolución de la llave ${pid}-${cod} (la tenía ${quien || "alguien"})?`))) return;
    try {
      const r = await devolverConAvisoRevision(alertaRevision, pid, cod);
      if (r.cancelado) return;
      if (!r.ok) { setExtraAviso({ tipo: "error", texto: r.error }); return; }
      setDevueltas((d) => [...d, cod]); setExtraTxt("");
      setExtraAviso({ tipo: "ok", texto: `✓ Devuelta la ${pid}-${cod}: ya está en el cajetín y queda marcada como que está.` });
      onDevuelta?.(cod);
    } catch (err) { setExtraAviso({ tipo: "error", texto: err.response?.data?.error || "No se pudo registrar la devolución." }); }
  };

  const nPuestos = valores.filter((v) => v !== "").length;
  const hayCambios = nPuestos > 0 || quitados.size > 0 || nuevas.length > 0;
  const hayErrores = avisos.some((a) => a?.tipo === "error");
  const extrasHechos = () => ({ quitados: llaves.filter((l, i) => quitados.has(i)).map((l) => l.llave_id), nuevas });
  const guardar = async () => {
    if (!hayCambios) { onClose(); return; }
    if (!nPuestos) { onHecho(0, [], extrasHechos()); return; }
    setGuardando(true);
    // Comprobar los que aún no se han comprobado (p.ej. el último, si no se pulsó Intro).
    for (let i = 0; i < llaves.length; i++) {
      if (valores[i] !== "" && !comprobados[i] && !(await comprobar(i))) { setIdx(i); setGuardando(false); return; }
    }
    try {
      const { data } = await api.post("/llaves/asignar_numeros", {
        asignaciones: llaves.map((l, i) => ({ llave_id: l.llave_id, numero: valores[i] })).filter((x) => x.numero !== ""),
      });
      if (data.ok) onHecho(data.asignadas, llaves.filter((l, i) => valores[i] !== "").map((l) => l.llave_id), extrasHechos());
      else avisar(data.error || "No se pudo guardar.");
    } catch (err) { avisar(err.response?.data?.error || "No se pudo guardar."); }
    finally { setGuardando(false); }
  };
  const cerrar = async () => {
    const n = nPuestos + quitados.size + nuevas.length;
    if (n && !(await confirmar(`Tienes ${n} cambio(s) sin guardar (números, llaves que no están o llaves a añadir). ¿Cerrar y descartarlos?`
      + (devueltas.length ? "\n\n(Las devoluciones que has registrado ya están guardadas.)" : "")))) return;
    onClose();
  };

  const fin = idx >= llaves.length;
  const actual = llaves[idx];
  return (
    <div className="modal-back" onMouseDown={cerrar}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>🔢 Asignar códigos</h3>
          <span className="tl-piso" style={{ fontSize: 13 }}>{pid} {piso}</span>
          <span className="grow" />
          <span className="sub">{Math.min(idx + 1, llaves.length)} de {llaves.length} sin número</span>
        </div>
        <div className="ac-libre">
          Siguiente número libre del piso: <b>{siguienteLibre ?? "…"}</b>
          {!fin && siguienteLibre != null && valores[idx] === "" && (
            <button className="btn btn-mini" onClick={() => setVal(idx, String(siguienteLibre))}>Usar {siguienteLibre}</button>
          )}
        </div>
        <div className="ac-pasos">
          {llaves.map((l, i) => (
            <button key={l.llave_id} type="button" onClick={() => setIdx(i)}
                    className={`ac-paso ${i === idx ? "actual" : ""} ${valores[i] !== "" ? "puesto" : ""} ${avisos[i]?.tipo === "error" ? "error" : ""} ${quitados.has(i) ? "quitado" : ""}`}>
              {i + 1}. {quitados.has(i) ? "✗ no está" : valores[i] !== "" ? `${pid}-${valores[i]}` : "—"}
            </button>
          ))}
        </div>
        {!fin ? (
          <div className="ac-actual">
            <label>Llave {idx + 1} de {llaves.length}{actual.preparada_para ? ` · preparada para ${actual.preparada_para}` : ""}</label>
            {quitados.has(idx) ? (
              <div className="ac-quitada">
                <span>✗ Esta llave <b>no está</b> en el cajetín: quedará tachada en el conteo y se dará de baja al registrar el cajetín.</span>
                <button type="button" className="btn btn-mini" onClick={() => toggleQuitado(idx)}>↩️ Sí está</button>
              </div>
            ) : (
              <>
                <div className="num-grande">
                  <span className="pref">{pid}-</span>
                  <input ref={inputRef} className="mono" inputMode="numeric" value={valores[idx]} placeholder=""
                         onChange={(e) => setVal(idx, e.target.value.replace(/\D/g, ""))}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); siguiente(); } }} />
                </div>
                <button type="button" className="ac-no-esta" onClick={() => toggleQuitado(idx)}>✕ Esta llave no está en el cajetín</button>
              </>
            )}
            {avisos[idx]?.texto && (
              <div className={`alert ${avisos[idx].tipo === "error" ? "alert-error" : avisos[idx].tipo === "ok" ? "alert-ok" : ""}`}
                   style={{ fontSize: 13, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span>{avisos[idx].tipo === "aviso" ? "⚠️ " : ""}{avisos[idx].texto}</span>
              </div>
            )}
            {avisos[idx] && (
              <OpcionesDevolverNumero pid={pid} numero={valores[idx]} existe={avisos[idx].existe}
                                      sinNumero={avisos[idx].valor === valores[idx] ? avisos[idx].sinNumero : null}
                                      onDevolver={(x) => devolverDesdeLlave(idx, x)} />
            )}
            <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>Intro: comprobar y pasar a la siguiente · déjalo vacío para saltar esta llave.</p>
          </div>
        ) : (
          <div className="alert alert-ok">✓ Has pasado por todas. {nPuestos ? `Se guardarán ${nPuestos} número(s).` : "No has puesto ningún número."}</div>
        )}
        <div className="cc-extra ac-extra">
          <label>＋ Añadir una llave que está en el cajetín y no sale aquí</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input className="mono" inputMode="numeric" value={extraTxt} placeholder="Código de la llave a añadir"
                   onChange={(e) => { setExtraTxt(e.target.value.replace(/\D/g, "")); setExtraAviso(null); }}
                   onKeyDown={(e) => { if (e.key === "Enter" && extraTxt.trim()) { e.preventDefault(); anadirExtra(); } }} />
            <button type="button" className="btn" onClick={anadirExtra} disabled={!extraTxt.trim()}>＋ Añadir</button>
          </div>
          {nuevas.length > 0 && (
            <div className="ac-nuevas">
              {nuevas.map((c) => (
                <span key={c} className="ac-nueva">✓ {pid}-{c} <small>añadir</small>
                  <button type="button" title="Quitar" onClick={() => setNuevas((x) => x.filter((y) => y !== c))}>✕</button>
                </span>
              ))}
            </div>
          )}
          {extraAviso?.tipo === "prestada" && (
            <div className="alert alert-error" style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span>La <b>{pid}-{extraAviso.cod}</b> consta <b>prestada a {extraAviso.quien || "alguien"}</b>. Si está en el cajetín, es que la han devuelto.</span>
              <button type="button" className="btn btn-mini btn-primary" onClick={devolverExtra}>↩️ Registrar devolución</button>
            </div>
          )}
          {extraAviso && extraAviso.tipo !== "prestada" && (
            <div className={`alert ${extraAviso.tipo === "ok" ? "alert-ok" : "alert-error"}`} style={{ marginTop: 8 }}>{extraAviso.texto}</div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={cerrar}>Cancelar</button>
          <button className="btn" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>‹ Anterior</button>
          {!fin && <button className="btn" onClick={siguiente} disabled={comprobando}>{comprobando ? "Comprobando…" : (valores[idx] === "" ? "Saltar ›" : "Siguiente ›")}</button>}
          <button className="btn btn-primary" onClick={guardar} disabled={guardando || hayErrores || !hayCambios}>
            {guardando ? "Guardando…"
              : `Guardar${[nPuestos && ` · ${nPuestos} número(s)`, quitados.size && ` · −${quitados.size} no está(n)`, nuevas.length && ` · +${nuevas.length}`].filter(Boolean).join("") || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Ficha breve de quién tiene una llave (Inventario > Quién).
function PersonaInfoModal({ fila, onClose }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!fila.tipo_persona || fila.id_persona == null) { setInfo({ tipo_persona: null }); return; }
    api.get("/persona_info", { params: { tipo_persona: fila.tipo_persona, id_persona: fila.id_persona } })
      .then(({ data }) => setInfo(data)).catch(() => setInfo({ tipo_persona: null }));
  }, [fila]);
  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
        <h3>👤 {fila.quien || "—"}</h3>
        {!info ? <p className="muted">Cargando…</p> : (
          <div className="info-grid">
            <div className="info-item"><span className="info-label">Tipo</span>
              <span className="info-value">{info.tipo_persona === "personal" ? "Personal" : info.tipo_persona === "alumno" ? "Residente" : "Sin identificar (solo nombre)"}</span></div>
            <div className="info-item"><span className="info-label">ID</span><span className="info-value mono">{info.id ?? fila.id_persona ?? "—"}</span></div>
            {info.tipo_persona === "personal" && (
              <>
                <div className="info-item"><span className="info-label">Tipo de personal</span><span className="info-value"><Badge tone="blue">{info.tipo || "—"}</Badge></span></div>
                <div className="info-item"><span className="info-label">Usuario</span><span className="info-value mono">{info.usuario}</span></div>
                {info.telefono ? <div className="info-item"><span className="info-label">Teléfono</span><span className="info-value">{info.telefono}</span></div> : null}
                <div className="info-item"><span className="info-label">Estado</span><span className="info-value">{info.activo ? <Badge tone="green">activo</Badge> : <Badge tone="red">baja</Badge>}</span></div>
              </>
            )}
            {info.tipo_persona === "alumno" && (
              <>
                <div className="info-item"><span className="info-label">Piso / habitación</span><span className="info-value">{info.piso ? `${info.piso_id} ${info.piso} · ${info.habitacion || ""}` : "—"}</span></div>
                <div className="info-item"><span className="info-label">Servicio · duración</span><span className="info-value">{[info.servicio, info.duracion].filter(Boolean).join(" · ") || "—"}</span></div>
                <div className="info-item"><span className="info-label">Estado</span><span className="info-value">{info.activo ? <Badge tone="green">activo</Badge> : <Badge tone="red">baja</Badge>}</span></div>
              </>
            )}
          </div>
        )}
        {info && !info.tipo_persona && <p className="muted" style={{ fontSize: 12.5 }}>Este préstamo solo tiene el nombre escrito: no está enlazado a una persona del personal ni a un residente.</p>}
        <div className="modal-actions"><button className="btn" onClick={onClose}>Cerrar</button></div>
      </div>
    </div>
  );
}

// Historial de UNA llave: todos sus préstamos y lo que se apuntó en cada uno.
function HistorialLlaveModal({ llaveId, onClose }) {
  const [datos, setDatos] = useState(null);
  useEffect(() => { api.get(`/llave/${llaveId}/historial`).then(({ data }) => setDatos(data)).catch(() => setDatos({ prestamos: [] })); }, [llaveId]);
  const tonoP = { prestada: "amber", preparado: "blue", devuelta: "green", perdida: "red", pedido: "gray" };
  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>📜 Historial de la llave</h3>
          {datos?.llave && <span className="tl-piso" style={{ fontSize: 13 }}>{datos.llave.id_piso}-{datos.llave.id_llave ?? "sin nº"} · {datos.llave.piso}</span>}
          <span className="grow" />
          {datos?.prestamos && <span className="sub">{datos.prestamos.length} préstamo(s)</span>}
        </div>
        {!datos ? <p className="muted">Cargando…</p> : datos.prestamos.length === 0 ? <div className="tl-vacio">Esta llave no se ha prestado nunca.</div> : (
          <ul className="timeline">
            {datos.prestamos.map((p) => (
              <li key={p.id} className="tl-item">
                <span className={`tl-ico t-${p.estado === "devuelta" ? "copia_creada" : p.estado === "perdida" ? "copia_perdida" : "comentario"}`}>{p.estado === "devuelta" ? "↩️" : p.estado === "perdida" ? "⛔" : "🔑"}</span>
                <div className="tl-body">
                  <div className="tl-top">
                    <span className="tl-tipo">{p.nombre_crudo || "—"}</span>
                    <span className="tl-resp">
                      {p.tipo_persona === "alumno" ? "Residente"
                        : p.tipo_persona === "personal" ? `Personal${p.tipo_personal ? ` · ${p.tipo_personal}` : ""}`
                        : "Sin identificar"}
                    </span>
                    <Badge tone={tonoP[p.estado] || "gray"}>{p.estado}</Badge>
                    <span className="tl-meta">{p.tipo_prestamo === "fija" ? "Fija" : "Provisional"}</span>
                  </div>
                  <div className="tl-det">
                    Salida: <b>{isoToEs(p.fecha_salida) || "—"}</b>
                    {p.estado !== "prestada" && <>{" · "}Devolución: <b>{isoToEs(p.fecha_devolucion) || "—"}</b></>}
                    {p.fecha_devolucion_estimada ? ` · Estimada: ${isoToEs(p.fecha_devolucion_estimada)}` : ""}
                    {(() => {
                      // "Responsable: X" (código de la importación del Excel) no se enseña:
                      // el tipo de persona ya sale en la etiqueta junto al nombre.
                      const com = (p.comentarios || "").replace(/(^|,\s*)Responsable:\s*[^,]*/gi, "").replace(/^[,\s]+|[,\s]+$/g, "");
                      return com ? `\n💬 ${com}` : "";
                    })()}
                    {(p.historial || []).length > 0 && "\n" + p.historial.map((h) => `· ${isoToEs(h.fecha)} — ${h.nota || h.campo}`).join("\n")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="modal-actions"><button className="btn" onClick={onClose}>Cerrar</button></div>
      </div>
    </div>
  );
}

// Poner número a una llave que no lo tiene (hueco). Solo una vez: después ya
// no se puede cambiar, así que se confirma. Propone el más bajo libre del
// piso; si el número existe en otro piso, avisa y pide confirmar otra vez.
function AsignarNumeroModal({ llave, onClose, onHecho }) {
  const [numero, setNumero] = useState("");
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [info, setInfo] = useState(null);   // {existe?, sinNumero?, valor} de /validar_numero
  const [hecho, setHecho] = useState(null); // mensaje tras devolver desde aquí
  const { confirmar, alertaRevision } = useDialog();

  // Al escribir el número se comprueba solo: si ya es de una llave prestada,
  // o el piso tiene prestadas sin número, se ofrece devolverla.
  useEffect(() => {
    if (!/^\d+$/.test(numero)) { setInfo(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/llave/${llave.llave_id}/validar_numero`, { params: { numero } });
        setInfo({ existe: data.ok ? null : data.existe, sinNumero: data.ok ? data.prestadas_sin_numero : null, valor: numero,
                  error: data.ok ? null : data.error });
      } catch { setInfo(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [numero, llave.llave_id]);

  const devolver = async ({ cod, mov_id, quien }) => {
    if (!(await confirmar(`¿Registrar la devolución de la llave ${llave.pid}-${cod} (la tenía ${quien || "alguien"})?`
      + (mov_id ? `\n\nEsa llave no tenía número en la app: se le pondrá el ${cod}.` : "")
      + "\n\nEsta llave sin número se quedará sin número (no era esa)."))) return;
    try {
      const r = await devolverConAvisoRevision(alertaRevision, llave.pid, cod, mov_id ? { mov_id } : {});
      if (r.cancelado) return;
      if (!r.ok) { setError(r.error); return; }
      setHecho(`✓ Devuelta la ${llave.pid}-${cod}: ya está en el cajetín. Esta llave sigue sin número; si no existe otra, márcala como que no está.`);
      setNumero(""); setInfo(null); setError(null);
    } catch (err) { setError(err.response?.data?.error || "No se pudo registrar la devolución."); }
  };

  useEffect(() => {
    api.get(`/llave/${llave.llave_id}/sugerir_numero`).then(({ data }) => { if (data.ok) setNumero(String(data.numero)); }).catch(() => {});
  }, [llave.llave_id]);

  const enviar = async (confirmado = false) => {
    setError(null);
    if (!/^\d+$/.test(numero)) { setError("Escribe un número entero (0 o mayor)."); return; }
    if (!confirmado && !(await confirmar(
      `Vas a asignar el número ${llave.pid}-${numero} a esta llave de ${llave.piso}.\n\nUna vez asignado NO se podrá cambiar. ¿Continuar?`))) return;
    setGuardando(true);
    try {
      const { data } = await api.post(`/llave/${llave.llave_id}/asignar_numero`, { numero, confirmado });
      if (data.ok) onHecho(); else setError(data.error || "No se pudo asignar.");
    } catch (err) {
      const d = err.response?.data;
      if (d?.requiere_confirmacion) {
        setGuardando(false);
        if (await confirmar(`⚠️ ${d.error}\n\n¿Asignarlo igualmente?`)) return enviar(true);
        return;
      }
      setError(d?.error || "No se pudo asignar.");
    } finally { setGuardando(false); }
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
        <h3>🔢 Asignar número</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Llave sin código de <b>{llave.piso}</b>{llave.cajetin ? ` · cajetín ${llave.cajetin}` : ""}.
          Se propone el número libre más bajo del piso.
        </p>
        <div className="num-grande">
          <span className="pref">{llave.pid}-</span>
          <input className="mono" inputMode="numeric" value={numero} autoFocus
                 onChange={(e) => setNumero(e.target.value.replace(/\D/g, ""))}
                 onKeyDown={(e) => e.key === "Enter" && enviar()} />
        </div>
        <div className="warn-box" style={{ fontSize: 13 }}>⚠️ Una vez asignado, el número de la llave <b>ya no se puede cambiar</b>.</div>
        {(error || info?.error) && <div className="alert alert-error" style={{ marginTop: 8 }}>{error || info.error}</div>}
        {info && info.valor === numero && (
          <OpcionesDevolverNumero pid={llave.pid} numero={numero} existe={info.existe} sinNumero={info.sinNumero} onDevolver={devolver} />
        )}
        {hecho && <div className="alert alert-ok" style={{ marginTop: 8 }}>{hecho}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={hecho ? onHecho : onClose}>{hecho ? "Cerrar" : "Cancelar"}</button>
          <button className="btn btn-primary" onClick={() => enviar()} disabled={guardando || !numero}>
            {guardando ? "Asignando…" : "Asignar número"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Inventario() {
  const [rows, setRows] = useState([]);
  const [ver, setVer] = useState("activas");
  const [infoLlaveId, setInfoLlaveId] = useState(null);
  const [crearJuego, setCrearJuego] = useState(false);
  const [revisarInfo, setRevisarInfo] = useState(null);
  const [soloRevisar, setSoloRevisar] = useState(false);
  const [perdida, setPerdida] = useState(null); // fila que se va a marcar como perdida
  const [soloSinCodigo, setSoloSinCodigo] = useState(false);
  const [verImportar, setVerImportar] = useState(false); // la parte de importar va plegada tras un botón
  const [asignar, setAsignar] = useState(null); // fila sin código a la que se va a poner número
  const [persona, setPersona] = useState(null);  // fila cuya persona se consulta
  const [histLlave, setHistLlave] = useState(null); // llave_id para el historial
  const { avisar, confirmar } = useDialog();

  const reactivar = async (r) => {
    if (!(await confirmar(`¿Reactivar la llave ${r.pid}-${r.codigo ?? "?"} (${r.piso})?\nVolverá a contar como llave activa en el cajetín.`))) return;
    try {
      const { data } = await api.post("/reactivar_juego", { llave_id: r.llave_id });
      if (data.ok) cargar(); else avisar(data.error || "No se pudo reactivar.");
    } catch (err) { avisar(err.response?.data?.error || "No se pudo reactivar."); }
  };

  const cargar = () => {
    api.get("/inventario_llaves", { params: { ver } })
      .then(({ data }) => setRows(data.rows || []));
  };
  useAutoBuscar(cargar, [ver]);
  useActualizarGlobal(cargar);

  const campos = [
    ["piso", "Piso", (r) => r.piso],
    ["id_piso", "ID piso", (r) => r.pid],
    ["llave", "Nº llave", (r) => r.codigo],
    ["cajetin", "Cajetín", (r) => r.cajetin],
    ["componentes", "Componentes", (r) => (r.componentes || []).map((c) => `${c.tipo} ${c.mecanismo}`).join(", ")],
    ["seguridad", "Seguridad", (r) => (r.es_seguridad ? `si ${r.seg_proveedor || ""}` : "no")],
    ["situacion", "Situación", (r) => SITUACION_LLAVE[r.situacion]?.[0] || "En cajetín"],
    ["quien", "Quién", (r) => r.quien],
  ];
  const rowsBase = rows.filter((r) => (!soloRevisar || r.revisar) && (!soloSinCodigo || r.codigo == null));
  const { filtros, setFiltro, limpiar, filtradas } = useFiltroColumnas(rowsBase, campos);
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(filtradas, [rowsBase, filtros]);
  const nSinCodigo = rows.filter((r) => r.codigo == null).length;

  return (
    <div className="card">
      <PanelHead title="🔑 Inventario de llaves" count={filtradas.length} total={rows.length} onLimpiar={limpiar} />
      <div className="row" style={{ marginBottom: 4 }}>
        <button className="btn-crear" style={{ flex: "0 0 auto" }} onClick={() => setCrearJuego(true)}>
          <span className="bc-ico">🔑</span>
          <span className="bc-txt"><b>Crear juego nuevo</b><small>Una o varias llaves para un piso</small></span>
          <span className="bc-mas">+</span>
        </button>
        <button className={`btn-soft ${verImportar ? "on" : ""}`} style={{ flex: "0 0 auto", marginLeft: "auto" }}
                onClick={() => setVerImportar((v) => !v)} aria-expanded={verImportar}>
          📥 Importar inventario {verImportar ? "▲" : "▼"}
        </button>
      </div>
      {verImportar && <ImportadorInventarioTemporal onImportado={cargar} />}
      <div className="row">
        <FiltroVer value={ver} onChange={setVer} opciones={[
          ["activas", "Activas"], ["bajas", "Perdidas/baja"], ["todas", "Todas"],
        ]} />
        <button className={`lg-venc ${soloRevisar ? "on" : ""}`} onClick={() => setSoloRevisar((v) => !v)}>🚩 Solo marcadas para revisión</button>
        <button className={`lg-venc ${soloSinCodigo ? "on" : ""}`} onClick={() => setSoloSinCodigo((v) => !v)}
                title="Llaves que aún no tienen número (huecos)">
          🔢 Solo sin código{nSinCodigo ? ` (${nSinCodigo})` : ""}
        </button>
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <FilterHead campos={campos} filtros={filtros} setFiltro={setFiltro} extra={<th></th>} />
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.llave_id} className={r.revisar ? "revisar" : ""}>
                <td className="strong">{r.piso}</td>
                <td className="mono muted">{r.pid}</td>
                <td className="mono">
                  {r.codigo ?? (r.situacion === "perdida" ? "—" : (
                    <button className="btn btn-mini acc-ambar" onClick={() => setAsignar(r)} title="Poner número a esta llave (solo una vez)">
                      🔢 Asignar
                    </button>
                  ))}
                </td>
                <td>{r.cajetin ?? <span className="muted">sin asignar</span>}</td>
                <td>
                  {(r.componentes || []).length > 0 ? (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {r.componentes.map((c) => <ChipComponente key={c.tipo} c={c} />)}
                    </div>
                  ) : <span className="muted">sin datos</span>}
                </td>
                <td>{r.es_seguridad ? <Badge tone="blue">🔒 {r.seg_proveedor || "Sí"}</Badge> : <span className="muted">—</span>}</td>
                <td title={r.situacion === "perdida" ? `${r.motivo_baja || ""}${r.fecha_baja ? ` · ${isoToEs(r.fecha_baja)}` : ""}` : undefined}>
                  <Badge tone={(SITUACION_LLAVE[r.situacion] || SITUACION_LLAVE.cajetin)[1]}>
                    {(SITUACION_LLAVE[r.situacion] || SITUACION_LLAVE.cajetin)[0]}
                  </Badge>
                </td>
                <td>
                  {r.quien
                    ? <button type="button" className="link-persona" onClick={() => setPersona(r)} title="Ver quién es">{r.quien}</button>
                    : <span className="muted">—</span>}
                </td>
                <td style={{ display: "flex", gap: 4 }}>
                  <button className="btn btn-mini" onClick={() => setInfoLlaveId(r.llave_id)} title="Ver/editar componentes de esta llave">ℹ️</button>
                  <button className="btn btn-mini" onClick={() => setHistLlave(r.llave_id)} title="Historial de préstamos de esta llave">📜</button>
                  <button className={`btn btn-mini btn-revisar ${r.revisar ? "on" : ""}`}
                          title={r.revisar ? `Marcada para revisión: ${r.revisar_nota || "(sin motivo)"}` : "Marcar para revisión"}
                          onClick={() => setRevisarInfo({ llave_id: r.llave_id, piso_id: r.pid, codigo: r.codigo, piso: r.piso, valor: !r.revisar, nota: "", alcance: "llave" })}>
                    {r.revisar ? "✅" : "🚩"}
                  </button>
                  {r.situacion === "perdida" ? (
                    <button className="btn btn-mini" onClick={() => reactivar(r)} title="Reactivar: ha aparecido o se marcó por error">↩️</button>
                  ) : (
                    <button className="btn btn-mini" onClick={() => setPerdida(r)} title="Marcar como perdida"
                            style={{ color: "#dc2626", borderColor: "#fca5a5" }}>⛔</button>
                  )}
                </td>
              </tr>
            ))}
            {filtradas.length === 0 && <tr><td colSpan={9} className="empty">🗂️ Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />
      {revisarInfo && <RevisarLlaveModal info={revisarInfo} onClose={() => setRevisarInfo(null)} onGuardado={cargar} />}
      {infoLlaveId && <InfoLlaveModal llaveId={infoLlaveId} onClose={() => setInfoLlaveId(null)} onGuardado={cargar} />}
      {perdida && <MarcarPerdidaModal llave={perdida} onClose={() => setPerdida(null)} onHecho={() => { setPerdida(null); cargar(); }} />}
      {asignar && <AsignarNumeroModal llave={asignar} onClose={() => setAsignar(null)} onHecho={() => { setAsignar(null); cargar(); }} />}
      {persona && <PersonaInfoModal fila={persona} onClose={() => setPersona(null)} />}
      {histLlave && <HistorialLlaveModal llaveId={histLlave} onClose={() => setHistLlave(null)} />}
      {crearJuego && (
        <CrearJuegoModal onClose={() => setCrearJuego(false)}
                         onCreado={() => { setCrearJuego(false); cargar(); }} />
      )}
    </div>
  );
}

// -------------------------------------------------------------- CREAR RUTA PARA OTRA PERSONA
// A diferencia de Rutas > Nueva solicitud (que siempre es para quien la
// crea), esto permite a un admin pedir llaves EN NOMBRE de un miembro del
// personal — la ruta queda a su nombre y le aparece en su propio "Mis
// solicitudes"/"Mis llaves" al iniciar sesión.
// -------------------------------------------------------------- CONTAR CAJETINES
// Ronda de arqueo físico: recorre los cajetines vigentes uno a uno, se
// cuenta a mano cuántas llaves hay dentro y se compara con lo que dice el
// sistema (llaves reales del piso − prestadas). Recuerda por dónde iba la
// ronda (conteo_estado) para poder cerrar y retomarla más tarde.
function ContarCajetines() {
  const [estado, setEstado] = useState(null); // resultado de /conteo_actual
  const [msg, setMsg] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [listaCaj, setListaCaj] = useState([]);
  const [buscaCaj, setBuscaCaj] = useState("");
  const [matchesCaj, setMatchesCaj] = useState([]);
  const navCaj = useNavegacionLista(matchesCaj, (x) => elegirCaj(x), () => setMatchesCaj([]));
  const inputRef = useRef(null);
  const { confirmar, alertaRevision } = useDialog();
  // Modo lista: {titulo, items:[{cajetin,pid,codigo}], idx, hechos:Set(cajetin)}.
  // Se cuentan solo esos cajetines, en ese orden, sin tocar la ronda normal.
  const [lista, setLista] = useState(null);
  // Lista interactiva del cajetín: marcas {llave_id: true(está) | false(no está)};
  // sin clave (undefined) = sin marcar todavía (gris).
  // y extras = códigos de llaves que sobran (la app no las tiene).
  const [marcas, setMarcas] = useState({});
  const [extras, setExtras] = useState([]);
  const [extraTxt, setExtraTxt] = useState("");
  const [extraAviso, setExtraAviso] = useState(null); // {tipo, texto, cod, quien}
  const [guardando, setGuardando] = useState(false);
  const ultimoCaj = useRef(null);
  const marcarCodigo = useRef(null); // código a dejar marcado "está" tras recargar (p.ej. tras devolverla)
  // Paso 1: meter cuántas llaves hay de verdad. Si cuadra, se registra sin más;
  // si no, se pasa a revisar la lista llave a llave (paso "revisar").
  const [paso, setPaso] = useState("contar");
  const [conteoReal, setConteoReal] = useState("");
  const [asignar, setAsignar] = useState(null); // llave sin código a la que poner número
  const [asignarLote, setAsignarLote] = useState(false); // asistente "Asignar códigos"
  const [modoCodigos, setModoCodigos] = useState(false); // modo "Asignar SOLO códigos"
  const conteoRef = useRef(null);
  const { confirmarPeligro, avisar } = useDialog();

  const cargar = (cajetin) => {
    setCargando(true);
    api.get("/conteo_actual", { params: cajetin ? { cajetin } : {} })
       .then(({ data }) => { setEstado(data); setCargando(false); })
       .catch(() => setCargando(false));
  };
  const empezarLista = (l) => {
    if (!l?.items?.length) return;
    setLista({ titulo: l.titulo, items: l.items, idx: 0, hechos: new Set() });
    setMsg(null);
    cargar(l.items[0].cajetin);
  };
  useEffect(() => {
    const pendiente = tomarListaConteo();   // llegó una lista antes de montarse esta pestaña
    if (pendiente) empezarLista(pendiente); else cargar();
    api.get("/conteo_lista").then(({ data }) => setListaCaj(data.rows || [])).catch(() => {});
    return onListaConteo(() => empezarLista(tomarListaConteo()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const irA = (idx) => {
    if (!lista || idx < 0 || idx >= lista.items.length) return;
    setLista((l) => ({ ...l, idx })); setMsg(null);
    cargar(lista.items[idx].cajetin);
  };
  // Tras contar uno de la lista: marcarlo hecho y pasar al siguiente pendiente.
  const siguienteDeLista = (texto) => {
    const hechos = new Set(lista.hechos).add(lista.items[lista.idx].cajetin);
    const sig = lista.items.findIndex((it, i) => i > lista.idx && !hechos.has(it.cajetin));
    const otro = sig >= 0 ? sig : lista.items.findIndex((it) => !hechos.has(it.cajetin));
    setLista((l) => ({ ...l, hechos, idx: otro >= 0 ? otro : l.idx }));
   
    if (otro >= 0) {
      setMsg({ tipo: "ok", texto });
      cargar(lista.items[otro].cajetin);
    } else {
      setMsg({ tipo: "ok", texto: `${texto} · 🎉 Lista terminada: ${hechos.size} cajetín(es) contados.` });
    }
  };
  const salirLista = async () => {
    const quedan = lista.items.length - lista.hechos.size;
    if (quedan > 0 && !(await confirmar(`Quedan ${quedan} cajetín(es) sin contar de la lista. ¿Salir de todas formas?`))) return;
    setLista(null); setMsg(null); cargar();
  };
  useActualizarGlobal(() => cargar());
  // Foco en "Llaves contadas" cada vez que se carga un cajetín nuevo (o se
  // vuelve a cargar el mismo tras resolver un descuadre) — `estado` es un
  // objeto nuevo cada vez que llega de la API, así que esto salta siempre.
  useEffect(() => {
    if (!estado || estado.vacio) return;
    const mismo = ultimoCaj.current === estado.cajetin;
    ultimoCaj.current = estado.cajetin;
    // Al llegar a un cajetín: todas SIN MARCAR (gris). Si se recarga el mismo,
    // se conservan las marcas ya puestas.
    setMarcas((prev) => {
      const m = {};
      for (const l of estado.llaves || []) {
        if (marcarCodigo.current != null && [].concat(marcarCodigo.current).map(String).includes(String(l.codigo))) m[l.llave_id] = true;
        else if (mismo && prev[l.llave_id] !== undefined) m[l.llave_id] = prev[l.llave_id];
      }
      return m;
    });
    marcarCodigo.current = null;
    if (!mismo) { setPaso("contar"); setConteoReal(""); }
    if (!mismo) { setExtras([]); setExtraTxt(""); setExtraAviso(null); }
    setTimeout(() => (mismo ? inputRef.current : conteoRef.current)?.focus(), 0);
  }, [estado]);

  // v: true (está) / false (no está); pulsar otra vez la misma opción la quita (gris).
  const marcar = (id, v) => setMarcas((m) => {
    const n = { ...m };
    if (n[id] === v) delete n[id]; else n[id] = v;
    return n;
  });
  const marcarTodas = (v) => setMarcas(v === undefined ? {} : Object.fromEntries((estado?.llaves || []).map((l) => [l.llave_id, v])));

  // Llave que sobra: se comprueba qué es antes de añadirla.
  const anadirExtra = async () => {
    const cod = extraTxt.trim();
    setExtraAviso(null);
    if (!cod) return;
    if (!/^\d+$/.test(cod)) { setExtraAviso({ tipo: "error", texto: "El código debe ser un número." }); return; }
    const enLista = (estado.llaves || []).find((l) => String(l.codigo) === cod);
    if (enLista) {
      setMarcas((m) => ({ ...m, [enLista.llave_id]: true }));
      setExtraAviso({ tipo: "ok", texto: `La ${estado.pid}-${cod} ya está en la lista: la he marcado como que está.` });
      setExtraTxt(""); return;
    }
    if (extras.includes(cod)) { setExtraTxt(""); return; }
    try {
      const { data } = await api.post("/conteo_previsualizar_ajuste", { piso_id: estado.piso_id, tipo: "alta", codigos: [cod] });
      const f = (data.filas || [])[0] || {};
      if (!f.ya_existe) { setExtras((x) => [...x, cod]); setExtraTxt(""); return; }
      if (f.situacion === "prestada") setExtraAviso({ tipo: "prestada", cod, quien: f.quien });
      else if (f.situacion === "preparada") setExtraAviso({ tipo: "error", texto: `La ${estado.pid}-${cod} está PREPARADA para ${f.quien || "alguien"}: cancela esa preparación en Gestionar si ya no se va a entregar.` });
      else if (f.situacion === "perdida") setExtraAviso({ tipo: "error", texto: `La ${estado.pid}-${cod} está dada de baja (perdida): si ha aparecido, reactívala en Inventario (↩️).` });
      else setExtraAviso({ tipo: "error", texto: `La ${estado.pid}-${cod} ya existe en este piso.` });
    } catch (err) { setExtraAviso({ tipo: "error", texto: err.response?.data?.error || "No se pudo comprobar el código." }); }
  };

  // Estaba prestada y está en el cajetín: registrar la devolución (como en Gestionar > Devolver).
  const devolverExtra = async () => {
    const { cod, quien } = extraAviso;
    if (!(await confirmar(`¿Registrar la devolución de la llave ${estado.pid}-${cod} (la tenía ${quien || "alguien"})?`))) return;
    try {
      const r = await devolverConAvisoRevision(alertaRevision, estado.pid, cod);
      if (r.cancelado) return;
      if (!r.ok) { setExtraAviso({ tipo: "error", texto: r.error }); return; }
      setExtraAviso({ tipo: "ok", texto: `✓ Devuelta la ${estado.pid}-${cod}: ya aparece en la lista, marcada como que está.` });
      setExtraTxt("");
      marcarCodigo.current = cod;
      refrescarLlaves();
    } catch (err) { setExtraAviso({ tipo: "error", texto: err.response?.data?.error || "No se pudo registrar la devolución." }); }
  };

  // Refresca SOLO la lista de llaves del cajetín actual (tras poner números o
  // registrar una devolución), sin el "Cargando…" ni volver a pintar el resto:
  // se conservan el paso, el número contado, las marcas y las llaves añadidas.
  const refrescarLlaves = async () => {
    const caj = estado?.cajetin;
    if (caj == null) return;
    try {
      const { data } = await api.get("/conteo_actual", { params: { cajetin: caj } });
      if (data.ok && !data.vacio && String(data.cajetin) === String(caj)) {
        setEstado((prev) => (prev && String(prev.cajetin) === String(caj)
          ? { ...prev, llaves: data.llaves, esperado: data.esperado } : prev));
      }
    } catch { /* si falla, se queda la lista que había */ }
  };

  // Envía el cuadre al backend y pasa al siguiente cajetín.
  const enviarCuadre = async (presentes, bajas, altas) => {
    setGuardando(true);
    try {
      const { data } = await api.post("/conteo_cuadrar", {
        piso_id: estado.piso_id, cajetin: estado.cajetin, manual: estado.manual || !!lista,
        presentes, bajas, altas, confirmacion: bajas.length ? "eliminar" : undefined,
      });
      if (!data.ok) { avisar(data.error || "No se pudo registrar."); return; }
      const texto = `✓ Cajetín ${estado.cajetin}: ${data.contado} llave(s)` +
        (data.bajas || data.altas ? ` · ${data.bajas} de baja, ${data.altas} nuevas` : " · cuadra");
      if (lista) { siguienteDeLista(texto); return; }
      setMsg({ tipo: "ok", texto });
      cargar(estado.manual ? estado.cajetin : undefined);
    } catch (err) {
      const d = err.response?.data;
      avisar(d?.error || "No se pudo registrar.");
      if (d?.obsoleta) cargar(estado.manual || lista ? estado.cajetin : undefined);
    } finally { setGuardando(false); }
  };

  // Paso 1: número real contado.
  const comprobarConteo = () => {
    const n = conteoReal.trim();
    if (!/^\d+$/.test(n) || guardando) return;
    if (Number(n) === Number(estado.esperado)) {
      enviarCuadre((estado.llaves || []).map((l) => l.llave_id), [], []);
    } else {
      setPaso("revisar");
      setMsg(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const registrarLista = async () => {
    if (!estado || estado.vacio || guardando) return;
    const llaves = estado.llaves || [];
    const sinMarcar = llaves.filter((l) => marcas[l.llave_id] === undefined);
    if (sinMarcar.length) {
      avisar(`Quedan ${sinMarcar.length} llave(s) sin marcar. Marca cada una como ✓ está o ✗ no está antes de registrar.`);
      return;
    }
    const presentes = llaves.filter((l) => marcas[l.llave_id] === true);
    const bajas = llaves.filter((l) => marcas[l.llave_id] === false);
    const etq = (l) => (l.codigo != null ? `${estado.pid}-${l.codigo}` : "sin número");
    if (bajas.length && !(await confirmarPeligro(
      `Vas a dar de BAJA (perdidas) ${bajas.length} llave(s) que no están en el cajetín ${estado.cajetin}:\n` +
      bajas.map((l) => `· ${etq(l)}`).join("\n") +
      "\n\nSi alguna la tiene alguien, regístrala como prestada en vez de darla de baja.",
      { titulo: "Llaves que faltan", boton: "Sí, dar de baja" }))) return;
    if (extras.length && !(await confirmar(
      `Se darán de ALTA ${extras.length} llave(s) a añadir: ${extras.map((c) => `${estado.pid}-${c}`).join(", ")}.\n\n¿Continuar?`))) return;
    await enviarCuadre(presentes.map((l) => l.llave_id), bajas.map((l) => l.llave_id), extras);
  };

  const reiniciar = async () => {
    if (!(await confirmar("¿Reiniciar la ronda de conteo desde el principio?"))) return;
    await api.post("/conteo_reset");
    setMsg(null);
    cargar();
  };

  const onBuscaCaj = (v) => {
    setBuscaCaj(v);
    if (!v.trim()) { setMatchesCaj([]); return; }
    const q = v.trim().toLowerCase();
    setMatchesCaj(listaCaj.filter((x) =>
      String(x.cajetin).toLowerCase().includes(q) || (x.codigo || "").toLowerCase().includes(q)
    ));
  };
  const elegirCaj = (x) => {
    setBuscaCaj(""); setMatchesCaj([]); setMsg(null);
    cargar(x.cajetin);
  };
  const volverARonda = () => { setBuscaCaj(""); setMsg(null); cargar(); };
  // Hacer que la ronda siga desde el cajetín elegido (en vez de desde donde iba).
  const seguirDesdeAqui = async () => {
    const caj = estado.cajetin;
    if (!(await confirmar(`La ronda de conteo seguirá desde el cajetín ${caj} (y luego en orden).\n\n¿Continuar?`))) return;
    try {
      const { data } = await api.post("/conteo_saltar", { cajetin: caj });
      if (data.ok) { setBuscaCaj(""); setMsg({ tipo: "ok", texto: `▶ La ronda sigue ahora desde el cajetín ${caj}.` }); cargar(); }
      else setMsg({ tipo: "error", texto: data.error || "No se pudo mover la ronda." });
    } catch (err) { setMsg({ tipo: "error", texto: err.response?.data?.error || "No se pudo mover la ronda." }); }
  };

  const botonModo = (
    <button type="button" className={`btn-solo-codigos ${modoCodigos ? "on" : ""}`} aria-pressed={modoCodigos}
            onClick={() => { if (modoCodigos) { setModoCodigos(false); cargar(); } else setModoCodigos(true); }}
            title={modoCodigos ? "Salir del modo Asignar SOLO códigos" : "Recorrer solo los cajetines con llaves sin código"}>
      {modoCodigos ? "✓ Asignar SOLO códigos · ACTIVO" : "🔢 Asignar SOLO códigos"}
    </button>
  );
  if (modoCodigos) {
    return (
      <div className="card card-solo-codigos">
        <div className="cc-titulo"><h3>🔢 Contar cajetines</h3>{botonModo}</div>
        <AsignarSoloCodigos desde={estado?.cajetin} onSalir={() => { setModoCodigos(false); cargar(); }} />
      </div>
    );
  }
  if (cargando || !estado) {
    return <div className="card"><h3>🔢 Contar cajetines</h3><p className="muted">Cargando…</p></div>;
  }
  if (estado.vacio) {
    return <div className="card"><h3>🔢 Contar cajetines</h3><p className="muted">No hay cajetines asignados todavía.</p></div>;
  }

  return (
    <div className="card">
      <div className="cc-titulo"><h3>🔢 Contar cajetines</h3>{botonModo}</div>
      <p className="muted">
        Recorre los cajetines en orden: cuenta a mano cuántas llaves hay y compáralo con lo que dice el sistema
        (llaves reales del piso menos las prestadas).
      </p>

      <div className="field" style={{ position: "relative", maxWidth: 280, marginBottom: 10 }}>
        <label>O ir directo a un cajetín</label>
        <input value={buscaCaj} placeholder="Número de cajetín o piso..." onChange={(e) => onBuscaCaj(e.target.value)}
               onKeyDown={navCaj.onKeyDown} onBlur={() => setTimeout(() => setMatchesCaj([]), 150)} autoComplete="off" />
        {matchesCaj.length > 0 && (
          <div className="deplist" ref={navCaj.listaRef}>
            {matchesCaj.map((x, i) => (
              <div {...navCaj.opcion(i)} key={x.cajetin} onMouseDown={(e) => { e.preventDefault(); elegirCaj(x); }}>
                Cajetín {x.cajetin} <span className="muted">· {x.codigo}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {lista && (
        <div className="conteo-lista">
          <div className="cl-top">
            <b>📋 Contando lista: {lista.titulo}</b>
            <span className="cl-prog">{lista.hechos.size} de {lista.items.length} contados</span>
            <span style={{ flex: 1 }} />
            <button className="btn btn-mini" onClick={() => irA(lista.idx - 1)} disabled={lista.idx <= 0}>‹ Anterior</button>
            <button className="btn btn-mini" onClick={() => irA(lista.idx + 1)} disabled={lista.idx >= lista.items.length - 1}>Saltar ›</button>
            <button className="btn btn-mini acc-rojo" onClick={salirLista}>Salir de la lista</button>
          </div>
          <div className="cl-chips">
            {lista.items.map((it, i) => (
              <button key={it.cajetin} type="button" onClick={() => irA(i)} title={`${it.pid} ${it.codigo}`}
                      className={`cl-chip ${i === lista.idx ? "actual" : ""} ${lista.hechos.has(it.cajetin) ? "hecho" : ""}`}>
                {lista.hechos.has(it.cajetin) ? "✓ " : ""}{it.cajetin}
              </button>
            ))}
          </div>
        </div>
      )}
      {estado.manual && !lista && (
        <div className="alert" style={{ marginBottom: 10, display: "flex", gap: 10, alignItems: "center" }}>
          🔎 Comprobación puntual — no afecta a tu progreso en la ronda.
          <span style={{ flex: 1 }} />
          <button className="btn btn-mini btn-primary" onClick={seguirDesdeAqui}
                  title="Que la ronda continúe a partir de este cajetín">▶ Seguir la ronda desde este cajetín</button>
          <button className="btn btn-mini" onClick={volverARonda}>Volver a la ronda</button>
        </div>
      )}

      {estado.ronda_completa && <div className="alert alert-ok">✓ Ronda completa — has vuelto al principio.</div>}
      <div className="cc-cabecera">
        <div className="cajbox-big">
          <span className="lbl">Cajetín</span>
          <span className="val">{estado.cajetin}</span>
        </div>
        <div className="cc-espera">
          <span className="ce-lbl">La app espera en este cajetín</span>
          <span className="ce-num">{estado.esperado}</span>
          <span className="ce-lbl">llave{Number(estado.esperado) === 1 ? "" : "s"}</span>
        </div>
      </div>
      <div className="cc-piso">
        <span className="cp-lbl">🏠 Piso</span>
        <span className="cp-nombre">{estado.codigo}</span>
        <span className="cp-id">ID {estado.pid}</span>
        <span className="cp-pos">{lista
          ? <>cajetín {lista.idx + 1} de {lista.items.length} de la lista</>
          : <>posición {estado.posicion} de {estado.total}</>}</span>
      </div>
      {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}

      {paso === "contar" && (
        <div className="cc-paso1">
          <label>¿Cuántas llaves hay realmente en el cajetín?</label>
          <div className="cp-fila">
            <input ref={conteoRef} className="cp-input" inputMode="numeric" value={conteoReal} placeholder="0"
                   onChange={(e) => setConteoReal(e.target.value.replace(/\D/g, ""))}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); comprobarConteo(); } }} />
            <button className="btn-registrar" style={{ maxWidth: 320 }} onClick={comprobarConteo} disabled={!conteoReal.trim() || guardando}>
              {guardando ? "Guardando…" : "Comprobar ›"}
            </button>
          </div>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 12.5 }}>
            Si coincide con lo que espera la app se registra y pasa al siguiente; si no, te pedirá revisar llave a llave.
          </p>
          <button className="btn-soft" style={{ marginTop: 10 }} onClick={() => setPaso("revisar")}>🔍 Revisar llave a llave igualmente</button>
          {(estado.llaves || []).some((l) => l.codigo == null) && (
            <button className="btn-soft" style={{ marginTop: 10, marginLeft: 8 }} onClick={() => setAsignarLote(true)}>
              🔢 Asignar códigos ({(estado.llaves || []).filter((l) => l.codigo == null).length} sin número)
            </button>
          )}
          {!lista && <button className="btn" style={{ marginTop: 10, marginLeft: 8 }} onClick={reiniciar}>Reiniciar ronda</button>}
        </div>
      )}

      {paso === "revisar" && conteoReal.trim() !== "" && Number(conteoReal) !== Number(estado.esperado) && (
        <div className="alert alert-error" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span>⚠️ Has contado <b>{conteoReal}</b> y la app espera <b>{estado.esperado}</b>. Revisa qué llaves no están o cuáles hay que añadir.</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn-mini" onClick={() => { setPaso("contar"); setTimeout(() => conteoRef.current?.focus(), 0); }}>‹ Volver a contar</button>
        </div>
      )}

      {paso === "revisar" && (() => {
        const llaves = estado.llaves || [];
        const nEstan = llaves.filter((l) => marcas[l.llave_id] === true).length;
        const nFaltan = llaves.filter((l) => marcas[l.llave_id] === false).length;
        const nPend = llaves.length - nEstan - nFaltan;
        const total = nEstan + extras.length;
        return (
          <>
            <div className="cc-lista">
              <div className="cc-lista-head">
                <b>🗝️ Llaves que la app tiene en este cajetín ({llaves.length})</b>
                <span className="muted">Marca cada una: <b>✓ está</b> o <b>✗ no está</b></span>
                <span style={{ flex: 1 }} />
                {llaves.length > 0 && (
                  <>
                    <button className="btn btn-mini" onClick={() => marcarTodas(true)}>✓ Todas están</button>
                    <button className="btn btn-mini" onClick={() => marcarTodas(false)}>✗ Ninguna</button>
                    <button className="btn btn-mini" onClick={() => marcarTodas(undefined)}>↺ Desmarcar</button>
                    {llaves.some((l) => l.codigo == null) && (
                      <button className="btn btn-mini acc-ambar" onClick={() => setAsignarLote(true)}>
                        🔢 Asignar códigos ({llaves.filter((l) => l.codigo == null).length})
                      </button>
                    )}
                  </>
                )}
              </div>
              {llaves.length === 0
                ? <div className="muted" style={{ padding: "10px 2px" }}>Según la app este cajetín debería estar <b>vacío</b>.</div>
                : (
                  <div className="cc-tiles">
                    {llaves.map((l) => {
                      const m = marcas[l.llave_id];
                      const clase = m === true ? "esta" : m === false ? "falta" : "pend";
                      return (
                        <div key={l.llave_id} className={`cc-tile ${clase}`}>
                          <span className="cc-tile-ico">{m === true ? "✓" : m === false ? "✗" : "?"}</span>
                          <span className="cc-tile-cod">{l.codigo != null ? l.codigo : "sin nº"}</span>
                          {l.preparada_para && <small>preparada · {l.preparada_para}</small>}
                          {l.codigo == null && (
                            <button type="button" className="cc-asignar" title="Poner número a esta llave"
                                    onClick={() => setAsignar({ llave_id: l.llave_id, pid: estado.pid, piso: estado.codigo, cajetin: estado.cajetin })}>
                              🔢 Asignar nº
                            </button>
                          )}
                          <div className="cc-tile-acc">
                            <button type="button" className={`ok ${m === true ? "on" : ""}`} aria-pressed={m === true}
                                    onClick={() => marcar(l.llave_id, true)} title="Está en el cajetín">✓ Está</button>
                            <button type="button" className={`no ${m === false ? "on" : ""}`} aria-pressed={m === false}
                                    onClick={() => marcar(l.llave_id, false)} title="No está en el cajetín">✗ No</button>
                          </div>
                        </div>
                      );
                    })}
                    {extras.map((cod) => (
                      <span key={`x-${cod}`} className="cc-tile nueva">
                        <span className="cc-tile-ico">✓</span>
                        <span className="cc-tile-cod">{cod}</span>
                        <small>añadir</small>
                        <button type="button" className="cc-tile-x" title="Quitar" onClick={() => setExtras((x) => x.filter((c) => c !== cod))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              {llaves.length === 0 && extras.length > 0 && (
                <div className="cc-tiles">
                  {extras.map((cod) => (
                    <span key={`x-${cod}`} className="cc-tile nueva">
                      <span className="cc-tile-ico">✓</span><span className="cc-tile-cod">{cod}</span><small>añadir</small>
                      <button type="button" className="cc-tile-x" title="Quitar" onClick={() => setExtras((x) => x.filter((c) => c !== cod))}>✕</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="cc-extra">
              <label>¿Hay alguna llave de más en el cajetín?</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input ref={inputRef} className="mono" inputMode="numeric" value={extraTxt} placeholder="Código de la llave a añadir"
                       onChange={(e) => { setExtraTxt(e.target.value); setExtraAviso(null); }}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (extraTxt.trim()) anadirExtra(); else registrarLista(); } }} />
                <button className="btn" onClick={anadirExtra} disabled={!extraTxt.trim()}>＋ Añadir</button>
              </div>
              {extraAviso?.tipo === "prestada" && (
                <div className="alert alert-error" style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span>La <b>{estado.pid}-{extraAviso.cod}</b> consta <b>prestada a {extraAviso.quien || "alguien"}</b>. Si está en el cajetín, es que la han devuelto.</span>
                  <button className="btn btn-mini btn-primary" onClick={devolverExtra}>↩️ Registrar devolución</button>
                </div>
              )}
              {extraAviso && extraAviso.tipo !== "prestada" && (
                <div className={`alert ${extraAviso.tipo === "ok" ? "alert-ok" : "alert-error"}`} style={{ marginTop: 8 }}>{extraAviso.texto}</div>
              )}
            </div>

            <div className={`cc-resumen ${nPend ? "pendiente" : nFaltan || extras.length ? "descuadre" : "cuadra"}`}>
              <span>Marcadas: <b>{total}</b></span>
              {conteoReal.trim() !== "" && <span>Contadas a mano: <b>{conteoReal}</b></span>}
              <span>El sistema espera: <b>{estado.esperado}</b></span>
              {nPend > 0 && <span className="r-pend">? {nPend} sin marcar</span>}
              {nFaltan > 0 && <span className="r-falta">✗ {nFaltan} no están → se darán de baja</span>}
              {extras.length > 0 && <span className="r-sobra">＋ {extras.length} a añadir → se darán de alta</span>}
              {!nPend && !nFaltan && !extras.length && <span className="r-ok">✓ Cuadra</span>}
            </div>

            <button className="btn-registrar" onClick={registrarLista} disabled={guardando || nPend > 0}>
              {guardando ? "Guardando…"
                : nPend > 0 ? `Marca las ${nPend} que faltan para poder registrar`
                : (nFaltan || extras.length ? "Registrar cambios y siguiente ›" : "✓ Cuadra · Registrar y siguiente ›")}
            </button>
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>Atajo: Intro en el campo vacío de "llave de más" registra.</p>
            {!lista && <button className="btn" style={{ marginTop: 4 }} onClick={reiniciar}>Reiniciar ronda</button>}
          </>
        );
      })()}
      {asignarLote && (
        <AsignarCodigosModal pid={estado.pid} pisoId={estado.piso_id} piso={estado.codigo}
                             llaves={(estado.llaves || []).filter((l) => l.codigo == null)}
                             extrasPadre={extras}
                             onClose={() => setAsignarLote(false)}
                             onDevuelta={(cod) => {
                               // Devuelta desde el asistente: aparece en la lista, marcada como que está.
                               marcarCodigo.current = [...[].concat(marcarCodigo.current ?? []), cod];
                               refrescarLlaves();
                             }}
                             onHecho={(n, ids, extra = {}) => {
                               setAsignarLote(false);
                               // Si les has puesto número es que las tienes en la mano: quedan marcadas como "está";
                               // las de "no está" quedan tachadas (se dan de baja al registrar el cajetín).
                               setMarcas((m) => ({ ...m,
                                 ...Object.fromEntries((ids || []).map((id) => [id, true])),
                                 ...Object.fromEntries((extra.quitados || []).map((id) => [id, false])) }));
                               if (extra.nuevas?.length) setExtras((x) => [...x, ...extra.nuevas.filter((c) => !x.includes(c))]);
                               const partes = [n && `${n} número(s) asignado(s)`, extra.quitados?.length && `${extra.quitados.length} tachada(s) (no está)`,
                                               extra.nuevas?.length && `${extra.nuevas.length} para añadir`].filter(Boolean);
                               setMsg({ tipo: "ok", texto: `✓ ${partes.join(" · ") || "Sin cambios"}.` });
                               refrescarLlaves();
                             }} />
      )}
      {asignar && (
        <AsignarNumeroModal llave={asignar} onClose={() => setAsignar(null)}
                            onHecho={() => { setAsignar(null); refrescarLlaves(); }} />
      )}
    </div>
  );
}

// Igual que Rutas > "Nueva solicitud" (CrearSolicitud): líneas de piso que
// se validan solas al pulsar Intro/salir del campo y se pintan en verde/rojo
// al momento, Intro añade línea, Backspace en una vacía la quita, pegar
// varias líneas las reparte. La única diferencia real es el selector de
// "para quién" (aquí la ruta no es para uno mismo) y que se envía a
// /rutas/crear_para en vez de /rutas/crear.
function CrearRutaAdmin() {
  const [personal, setPersonal] = useState([]);
  const [busca, setBusca] = useState("");
  const [matches, setMatches] = useState([]);
  const navPara = useNavegacionLista(matches, (x) => elegir(x), () => setMatches([]));
  // elegido: {tipo:'cuenta', user, nombre, apellidos} (personal con cuenta)
  //       o  {tipo:'residente', id_persona, nombre} (de ch_base.alumnos).
  // Si no se elige nada de la lista, vale el texto escrito (texto libre, como
  // en Prestar): la ruta queda para esa persona sin cuenta.
  const [elegido, setElegido] = useState(null);
  const buscaTimer = useRef(null);
  const [lineas, setLineas] = useState([{ texto: "", estado: null }]);
  const [devol, setDevol] = useState("");
  const [tipoPrestamo, setTipoPrestamo] = useState("provisional");
  const [msg, setMsg] = useState(null);
  const [creando, setCreando] = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    api.get("/personal").then(({ data }) => setPersonal((data.rows || []).filter((r) => r.activo)));
  }, []);

  const onBusca = (v) => {
    setBusca(v); setElegido(null);
    clearTimeout(buscaTimer.current);
    if (!v.trim()) { setMatches([]); return; }
    const q = v.trim().toLowerCase();
    const deCuenta = personal.filter((p) =>
      `${p.nombre} ${p.apellidos || ""}`.toLowerCase().includes(q) || p.user.toLowerCase().includes(q)
    ).map((p) => ({ tipo: "cuenta", ...p }));
    setMatches(deCuenta);
    // Residentes: mismo buscador que Prestar (/buscar_persona).
    buscaTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/buscar_persona", { params: { q: v.trim() } });
        const residentes = (data || []).filter((x) => x.tipo_persona === "alumno")
          .map((x) => ({ tipo: "residente", id_persona: x.id_persona, nombre: x.nombre }));
        setMatches((prev) => [...prev.filter((m) => m.tipo === "cuenta"), ...residentes]);
      } catch { /* sin residentes: queda la lista de personal */ }
    }, 250);
  };
  const elegir = (p) => {
    setElegido(p);
    setBusca(p.tipo === "cuenta" ? `${p.nombre} ${p.apellidos || ""}`.trim() : p.nombre);
    setMatches([]);
  };

  const validarLinea = async (i, textoLinea) => {
    if (!textoLinea.trim()) return;
    try {
      const { data } = await api.post("/rutas/previsualizar", { texto: textoLinea });
      const fila = (data.filas || [])[0] || null;
      setLineas((prev) => {
        if (!prev[i] || prev[i].texto !== textoLinea) return prev;
        const copia = [...prev];
        copia[i] = { texto: textoLinea, estado: fila };
        return copia;
      });
    } catch {
      // sin conexión: deja la línea sin validar, se revalidará al crear
    }
  };

  const onChangeLinea = (i, valor) => {
    setLineas((prev) => {
      const copia = [...prev];
      copia[i] = { texto: valor, estado: null };
      return copia;
    });
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const textoLinea = e.target.value;
      validarLinea(i, textoLinea);
      setLineas((prev) => (i === prev.length - 1 ? [...prev, { texto: "", estado: null }] : prev));
      setTimeout(() => refs.current[i + 1]?.focus(), 0);
    } else if (e.key === "Backspace" && e.target.value === "" && lineas.length > 1) {
      e.preventDefault();
      setLineas((prev) => prev.filter((_, idx) => idx !== i));
      setTimeout(() => refs.current[Math.max(0, i - 1)]?.focus(), 0);
    }
  };

  // Piso elegido del desplegable: la línea queda "id abreviatura", se valida
  // y se pasa a la siguiente (como al pulsar Intro).
  const elegirPisoLinea = (i, p) => {
    const t = textoPiso(p);
    setLineas((prev) => {
      const copia = [...prev];
      copia[i] = { texto: t, estado: null };
      return i === copia.length - 1 ? [...copia, { texto: "", estado: null }] : copia;
    });
    validarLinea(i, t);
    setTimeout(() => refs.current[i + 1]?.focus(), 0);
  };

  const quitarLinea = (i) => {
    setLineas((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
  };

  const onPasteLinea = (i, e) => {
    const texto = e.clipboardData.getData("text");
    if (!texto.includes("\n")) return;
    e.preventDefault();
    const partes = texto.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!partes.length) return;
    setLineas((prev) => {
      const copia = [...prev];
      copia.splice(i, 1, ...partes.map((t) => ({ texto: t, estado: null })));
      if ((copia[copia.length - 1]?.texto || "").trim()) copia.push({ texto: "", estado: null });
      return copia;
    });
    partes.forEach((t, idx) => validarLinea(i + idx, t));
    setTimeout(() => refs.current[i + partes.length]?.focus(), 0);
  };

  const crear = async () => {
    setMsg(null);
    if (!elegido && !busca.trim()) { setMsg({ tipo: "error", texto: "Escribe o elige para quién es la ruta." }); return; }
    const conTexto = lineas.filter((l) => l.texto.trim());
    if (!conTexto.length) { setMsg({ tipo: "error", texto: "Escribe al menos un piso." }); return; }
    if (!devol) { setMsg({ tipo: "error", texto: "La devolución estimada es obligatoria." }); return; }
    if (devol < hoyIso()) { setMsg({ tipo: "error", texto: "La devolución estimada no puede ser anterior a hoy." }); return; }
    setCreando(true);
    let filas;
    try {
      const { data } = await api.post("/rutas/previsualizar", { texto: conTexto.map((l) => l.texto).join("\n") });
      filas = data.filas || [];
    } catch {
      setMsg({ tipo: "error", texto: "No se pudo comprobar los pisos." });
      setCreando(false);
      return;
    }
    setLineas([...filas.map((f) => ({ texto: `${f.id_origen_txt} ${f.codigo_piso || ""}`.trim(), estado: f })),
               { texto: "", estado: null }]);
    const noEncontrados = filas.filter((f) => !f.existe);
    if (noEncontrados.length) {
      const lista = noEncontrados.map((f) => f.id_origen_txt || "(vacío)").join(", ");
      setMsg({ tipo: "error", texto: `No se encuentran estos pisos: ${lista}. Corrígelos (mira las líneas en rojo).` });
      setCreando(false);
      return;
    }
    const lineasPost = filas.map((f) => ({ id_origen_txt: f.id_origen_txt, codigo_piso: f.codigo_piso, fecha_devol: devol, tipo_prestamo: tipoPrestamo }));
    try {
      const para = elegido?.tipo === "cuenta"
        ? { user: elegido.user }
        : { persona: {
            nombre: busca.trim(),
            tipo_persona: elegido?.tipo === "residente" ? "alumno" : null,
            id_persona: elegido?.tipo === "residente" ? elegido.id_persona : null,
          } };
      const { data } = await api.post("/rutas/crear_para", { ...para, lineas: lineasPost });
      if (data.ok) {
        setMsg({ tipo: "ok", texto: `Ruta creada (nº ${data.ruta_id}) para ${data.para} · ${data.creadas} línea(s).` });
        setLineas([{ texto: "", estado: null }]); setDevol(""); setTipoPrestamo("provisional"); setElegido(null); setBusca("");
      } else {
        setMsg({ tipo: "error", texto: data.error || "No se pudo crear." });
      }
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "No se pudo crear la ruta." });
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="card">
      <h3>📝 Crear ruta para otra persona</h3>
      <p className="muted">
        Pide llaves en nombre de otra persona. Si es alguien del personal con cuenta, le aparecerá en su propio
        "Mis solicitudes". También puede ser un residente o cualquier nombre escrito a mano (como en Prestar).
      </p>
      {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}

      <div className="field" style={{ position: "relative", maxWidth: 360 }}>
        <label>Para quién</label>
        <input value={busca} placeholder="Personal, residente o nombre libre"
               onChange={(e) => onBusca(e.target.value)} onKeyDown={navPara.onKeyDown}
               onBlur={() => setTimeout(() => setMatches([]), 150)} autoComplete="off" />
        {matches.length > 0 && (
          <div className="deplist" ref={navPara.listaRef}>
            {matches.map((p, i) => (
              <div {...navPara.opcion(i)} key={p.tipo === "cuenta" ? `u-${p.user}` : `r-${p.id_persona}`}
                   onMouseDown={(e) => { e.preventDefault(); elegir(p); }}>
                {p.tipo === "cuenta"
                  ? <>{p.nombre} {p.apellidos || ""} <span className="muted">· personal · {p.user}</span></>
                  : <>{p.nombre} <span className="muted">· residente</span></>}
              </div>
            ))}
          </div>
        )}
        {elegido?.tipo === "cuenta" && <div className="field-feedback ok">✓ Personal con cuenta · {elegido.user}</div>}
        {elegido?.tipo === "residente" && <div className="field-feedback ok">✓ Residente</div>}
        {!elegido && busca.trim() && (
          <div className="field-feedback" style={{ color: "#64748b" }}>Texto libre: persona sin cuenta</div>
        )}
      </div>

      <label>Pisos (nº de piso y opcionalmente el código — pulsa Intro para pasar a la siguiente línea)</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {lineas.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <PisoAutocomplete
              inputRef={(el) => { refs.current[i] = el; }}
              className={`linea-piso ${l.estado ? (l.estado.existe ? "linea-ok" : "linea-error") : ""}`}
              wrapStyle={{ flex: 1 }}
              style={{ width: "100%" }}
              value={l.texto}
              placeholder={i === 0 ? "ID o abreviatura: 29 / AB1.1B(1)" : i === 1 ? "42" : "264    SJB20.2D (15)"}
              onChange={(v) => onChangeLinea(i, v)}
              onSelect={(p) => elegirPisoLinea(i, p)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onPaste={(e) => onPasteLinea(i, e)}
              onBlur={(e) => validarLinea(i, e.target.value)}
            />
            {l.estado && (
              <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                {l.estado.existe ? `✓ ${l.estado.codigo_real || ""}` : "✗ no encontrado"}
              </span>
            )}
            {lineas.length > 1 && (
              <button type="button" className="btn btn-mini" onClick={() => quitarLinea(i)}>✕</button>
            )}
          </div>
        ))}
      </div>

      <div className="row">
        <div>
          <label>Devolución estimada *</label>
          <input type="date" value={devol} min={hoyIso()} onChange={(e) => setDevol(e.target.value)} required />
        </div>
        <div>
          <label>Tipo</label>
          <div className="seg-buttons">
            <button type="button" className={tipoPrestamo === "provisional" ? "on" : ""} onClick={() => setTipoPrestamo("provisional")}>Provisional</button>
            <button type="button" className={tipoPrestamo === "fija" ? "on" : ""} onClick={() => setTipoPrestamo("fija")}>Fija</button>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button className="btn btn-primary" onClick={crear} disabled={creando}>
          {creando ? "Creando…" : "Crear ruta"}
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ PERSONAL (solo lectura)
function Personal() {
  const [rows, setRows] = useState([]);
  const [crear, setCrear] = useState(false);
  const [editar, setEditar] = useState(null); // fila en edición, o null
  const { avisar, confirmar } = useDialog();

  const cargar = () => api.get("/personal").then(({ data }) => setRows(data.rows || []));
  useEffect(() => { cargar(); }, []);
  useActualizarGlobal(cargar);

  const campos = [
    ["id", "ID", (r) => r.id_empleado],
    ["usuario", "Usuario", (r) => r.user],
    ["nombre", "Nombre", (r) => `${r.nombre || ""} ${r.apellidos || ""}`],
    ["tipo", "Tipo", (r) => r.tipo],
    ["telefono", "Teléfono", (r) => r.telefono],
    ["estado", "Estado", (r) => (r.activo ? "activo" : "baja")],
  ];
  const { filtros, setFiltro, limpiar, filtradas } = useFiltroColumnas(rows, campos);
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(filtradas, [rows, filtros]);

  const baja = async (r) => {
    if (!(await confirmar(`¿Dar de baja a ${r.nombre}? Dejará de poder iniciar sesión de inmediato.`))) return;
    const { data } = await api.post(`/personal/${encodeURIComponent(r.user)}/baja`);
    if (data.ok) cargar(); else avisar(data.error || "No se pudo dar de baja.");
  };
  const reactivar = async (r) => {
    if (!(await confirmar(`¿Reactivar a ${r.nombre}? Podrá volver a iniciar sesión.`))) return;
    const { data } = await api.post(`/personal/${encodeURIComponent(r.user)}/reactivar`);
    if (data.ok) cargar(); else avisar(data.error || "No se pudo reactivar.");
  };

  return (
    <div className="card">
      <PanelHead title="👥 Personal" count={filtradas.length} total={rows.length} onLimpiar={limpiar} />
      <div className="row" style={{ marginBottom: 4 }}>
        <button className="btn btn-primary" style={{ flex: "0 0 auto" }} onClick={() => setCrear(true)}>
          + Crear usuario
        </button>
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <FilterHead campos={campos} filtros={filtros} setFiltro={setFiltro} extra={<th></th>} />
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.user} className={r.activo ? "" : "fila-baja"} title={r.activo ? undefined : "Dado de baja"}>
                <td className="mono">{r.id_empleado ?? <span className="muted" title="Sin id de empleado (usuario tipo 'llaves')">—</span>}</td>
                <td className="mono">{r.user}</td>
                <td className="strong">{r.nombre} {r.apellidos || ""}</td>
                <td>{r.tipo ? <Badge tone="blue">{r.tipo}</Badge> : <span className="muted">—</span>}</td>
                <td>{r.telefono ?? <span className="muted">—</span>}</td>
                <td>{r.activo ? <Badge tone="green">activo</Badge> : (
                  <>
                    <Badge tone="red">🚫 baja</Badge>
                    {r.fecha_baja && <div className="fb-fecha">desde {isoToEs(String(r.fecha_baja).slice(0, 10))}</div>}
                  </>
                )}</td>
                <td className="acc-cell">
                  {r.tipo === "llaves" ? (
                    <>
                      <button className="btn btn-mini" onClick={() => setEditar(r)} title="Editar datos">✏️ Editar</button>
                      {r.activo ? (
                        <button className="btn btn-mini btn-danger" onClick={() => baja(r)} title="Dar de baja (bloquea el acceso)">🚫 Baja</button>
                      ) : (
                        <button className="btn btn-mini btn-success" onClick={() => reactivar(r)} title="Reactivar acceso">✅ Reactivar</button>
                      )}
                    </>
                  ) : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
            {filtradas.length === 0 && <tr><td colSpan={7} className="empty">🗂️ Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />

      {crear && <PersonalForm onClose={() => setCrear(false)} onGuardado={() => { setCrear(false); cargar(); }} />}
      {editar && (
        <PersonalForm fila={editar} onClose={() => setEditar(null)}
                      onGuardado={() => { setEditar(null); cargar(); }} />
      )}
    </div>
  );
}

// Modal de alta/edición de un usuario tipo 'llaves' (la única escritura que
// esta app hace en app_compras.users).
function PersonalForm({ fila, onClose, onGuardado }) {
  const editando = !!fila;
  const [user, setUser] = useState(fila?.user || "");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [nombre, setNombre] = useState(fila?.nombre || "");
  const [apellidos, setApellidos] = useState(fila?.apellidos || "");
  const [telefono, setTelefono] = useState(fila?.telefono ?? "");
  const [idEmpleado, setIdEmpleado] = useState(fila?.id_empleado ?? "");
  const [msg, setMsg] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setMsg(null);
    if (!editando && (!user.trim() || !nombre.trim() || !password)) {
      setMsg({ tipo: "error", texto: "Usuario, nombre y contraseña son obligatorios." });
      return;
    }
    if (password && password !== password2) {
      setMsg({ tipo: "error", texto: "Las dos contraseñas no coinciden." });
      return;
    }
    setGuardando(true);
    try {
      if (editando) {
        const body = { nombre, apellidos, telefono, id_empleado: (idEmpleado ?? "").toString().trim() || null };
        if (password) body.password = password;
        const { data } = await api.post(`/personal/${encodeURIComponent(fila.user)}/editar`, body);
        if (data.ok) onGuardado();
        else setMsg({ tipo: "error", texto: data.error || "No se pudo guardar." });
      } else {
        const { data } = await api.post("/personal/crear", {
          user, password, nombre, apellidos, telefono,
          id_empleado: (idEmpleado ?? "").toString().trim() || null,
        });
        if (data.ok) onGuardado();
        else setMsg({ tipo: "error", texto: data.error || "No se pudo crear." });
      }
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "Error de conexión." });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
        <h3>{editando ? `✏️ Editar ${fila.user}` : "🔑 Nuevo usuario de llaves"}</h3>
        <p className="muted">Acceso limitado: solo puede enviar solicitudes en Rutas.</p>
        {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}

        <label>Usuario</label>
        <input value={user} onChange={(e) => setUser(e.target.value)} disabled={editando} autoFocus={!editando} />
        <label>Nombre</label>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <label>Apellidos</label>
        <input value={apellidos} onChange={(e) => setApellidos(e.target.value)} />
        <label>Teléfono</label>
        <input value={telefono} onChange={(e) => setTelefono(e.target.value)} inputMode="numeric" />
        <label>id_empleado (opcional)</label>
        <input value={idEmpleado} onChange={(e) => setIdEmpleado(e.target.value)} inputMode="numeric"
               placeholder="Solo si ya existe como empleado en el CRM." />
        <label>{editando ? "Nueva contraseña (déjalo en blanco para no cambiarla)" : "Contraseña"}</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {(password || !editando) && (
          <>
            <label>Confirmar contraseña</label>
            <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} />
          </>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ CLIENTES (solo lectura)
function Clientes() {
  const [rows, setRows] = useState([]);
  const [ver, setVer] = useState("activos");
  const cargar = () => api.get("/clientes", { params: { ver } }).then(({ data }) => setRows(data.rows || []));
  useAutoBuscar(cargar, [ver]);
  useActualizarGlobal(cargar);

  const campos = [
    ["id", "ID", (r) => r.id_alumno],
    ["nombre", "Nombre", (r) => r.nombre],
    ["piso", "ID piso", (r) => r.id_piso],
    ["piso_nombre", "Piso", (r) => r.piso_nombre],
    ["duracion", "Duración", (r) => r.duracion],
    ["estado", "Estado", (r) => (r.activo ? "activo" : "baja")],
  ];
  const { filtros, setFiltro, limpiar, filtradas } = useFiltroColumnas(rows, campos);
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(filtradas, [rows, filtros]);

  return (
    <div className="card">
      <PanelHead title="🏠 Clientes / residentes" count={filtradas.length} total={rows.length} onLimpiar={limpiar} />
      <p className="muted">Datos de solo lectura (vienen de la base de datos de residentes, ch_base).</p>
      <div className="row">
        <FiltroVer value={ver} onChange={setVer} opciones={[
          ["activos", "Activos"], ["bajas", "Bajas"], ["todos", "Todos"],
        ]} />
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <FilterHead campos={campos} filtros={filtros} setFiltro={setFiltro} />
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={`${r.id_alumno}-${r.id_piso ?? ""}-${r.habitacion ?? ""}`}>
                <td className="mono">{r.id_alumno}</td>
                <td className="strong">{r.nombre}</td>
                <td className="mono">{r.id_piso ?? <span className="muted">—</span>}</td>
                <td>{r.piso_nombre || <span className="muted">—</span>}</td>
                <td>{r.duracion || <span className="muted">—</span>}</td>
                <td>{r.activo ? <Badge tone="green">activo</Badge> : <Badge tone="red">baja</Badge>}</td>
              </tr>
            ))}
            {filtradas.length === 0 && <tr><td colSpan={6} className="empty">🗂️ Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />
    </div>
  );
}

// ------------------------------------------------------------------ COMPRAS
// Compras de llaves (tabla `compras` propia de esta app, en app_llaves — NO
// confundir con app_compras.compras, que es de un sistema distinto). Cada
// compra tiene un lugar y una lista de productos (portal/piso/buzón,
// mecanismo, fecha, precio), opcionalmente ligados a un piso.
const NOMBRE_TIPO_PRODUCTO = { portal: "Portal", piso: "Piso", buzon: "Buzón" };

function productoVacio() {
  return { id_piso: null, piso_codigo: "", tipo: "portal", mecanismo: "fisica", unidades: 1, precio: "" };
}

function Compras() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  // Filtro por piso: texto del buscador + piso elegido del desplegable.
  const [pisoTxt, setPisoTxt] = useState("");
  const [pisoFiltro, setPisoFiltro] = useState(null); // {id_origen, codigo}
  const [modal, setModal] = useState(null); // {id?, lugar, fecha, productos: [...]}
  const { avisar, confirmar } = useDialog();

  const cargar = () => {
    const params = {};
    if (q) params.q = q;
    if (pisoFiltro) params.id_piso = pisoFiltro.id_origen;
    return api.get("/compras", { params }).then(({ data }) => setRows(data.rows || []));
  };
  useAutoBuscar(cargar, [q, pisoFiltro]);
  useActualizarGlobal(cargar);

  const nuevaCompra = () => setModal({ lugar: "", fecha: hoyIso(), productos: [productoVacio()] });
  const editarCompra = (r) => setModal({ id: r.id, lugar: r.lugar || "", fecha: r.fecha || "", productos: r.productos.map((p) => ({ ...p })) });

  const borrar = async (r) => {
    if (!(await confirmar(`¿Borrar esta compra (${r.lugar || "sin lugar"}, ${r.n_productos} producto(s))? No se puede deshacer.`))) return;
    await api.post(`/compras/${r.id}/borrar`);
    cargar();
  };

  const guardar = async (datos) => {
    const productos = datos.productos.map((p) => ({
      id_piso: p.id_piso || null, tipo: p.tipo, mecanismo: p.mecanismo,
      unidades: Math.max(1, parseInt(p.unidades, 10) || 1),
      precio: p.precio === "" || p.precio == null ? null : Number(p.precio),
    }));
    const payload = { lugar: datos.lugar, fecha: datos.fecha || null, productos };
    try {
      const { data } = datos.id
        ? await api.post(`/compras/${datos.id}`, payload)
        : await api.post("/compras", payload);
      if (data.ok) { setModal(null); cargar(); }
      else avisar(data.error || "No se pudo guardar.");
    } catch (err) {
      avisar(err.response?.data?.error || "No se pudo guardar.");
    }
  };

  // Todo llega del servidor (sin tope); se pinta de 100 en 100.
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(rows, [rows]);
  return (
    <div className="card">
      <PanelHead title="🧾 Compras" count={rows.length} total={rows.length}
                 onLimpiar={() => { setQ(""); setPisoTxt(""); setPisoFiltro(null); }} />
      <div className="row" style={{ marginBottom: 4, alignItems: "center" }}>
        <button className="btn btn-primary" style={{ flex: "0 0 auto" }} onClick={nuevaCompra}>+ Nueva compra</button>
        <input placeholder="Filtrar por lugar..." value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 220 }} />
        <div style={{ display: "flex", gap: 4, alignItems: "center", flex: "0 1 260px" }}>
          <PisoAutocomplete
            wrapStyle={{ flex: 1 }}
            placeholder="Filtrar por piso (ID o abreviatura)..."
            className={pisoFiltro ? "input-ok" : ""}
            value={pisoTxt}
            onChange={(v) => { setPisoTxt(v); if (pisoFiltro) setPisoFiltro(null); }}
            onSelect={(p) => setPisoFiltro(p)}
          />
          {(pisoTxt || pisoFiltro) && (
            <button className="btn btn-mini" title="Quitar filtro de piso"
                    onClick={() => { setPisoTxt(""); setPisoFiltro(null); }}>✕</button>
          )}
        </div>
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <tr><th>Fecha</th><th>Lugar</th><th>Productos</th><th>Total</th><th></th></tr>
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.id}>
                <td>{isoToEs(r.fecha) || <span className="muted">—</span>}</td>
                <td className="strong">{r.lugar || <span className="muted">—</span>}</td>
                <td>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {r.productos.map((p, i) => (
                      <Badge key={i} tone="gray">
                        {(p.unidades || 1) > 1 ? `${p.unidades}× ` : ""}
                        {NOMBRE_TIPO_PRODUCTO[p.tipo] || p.tipo} · {p.mecanismo === "magnetica" ? "🧲" : "🗝️"}
                        {p.id_piso ? ` · ${p.id_piso} ${p.piso_codigo || ""}` : ""}
                        {p.precio != null ? ` · ${p.precio}€${(p.unidades || 1) > 1 ? "/ud" : ""}` : ""}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="num-cell">{r.total ? `${r.total.toFixed(2)}€` : "—"}</td>
                <td style={{ display: "flex", gap: 4 }}>
                  <button className="btn btn-mini" onClick={() => editarCompra(r)} title="Editar compra">✏️</button>
                  <button className="btn btn-mini btn-danger" onClick={() => borrar(r)} title="Borrar compra">🗑️</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="empty">🗂️ Sin compras registradas.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />
      {modal && <CompraModal datos={modal} onClose={() => setModal(null)} onGuardar={guardar} />}
    </div>
  );
}

function CompraModal({ datos, onClose, onGuardar }) {
  const [lugar, setLugar] = useState(datos.lugar || "");
  const [fecha, setFecha] = useState(datos.fecha || "");
  const [productos, setProductos] = useState(datos.productos);
  const { avisar, confirmar } = useDialog();

  const setP = (i, campo, val) => setProductos((prev) => prev.map((p, idx) => (idx === i ? { ...p, [campo]: val } : p)));
  const quitarP = (i) => setProductos((prev) => prev.filter((_, idx) => idx !== i));

  // Lugar y el piso de cada producto son obligatorios: sin eso no tiene
  // sentido ni añadir otro producto ni guardar la compra.
  const faltaLugar = !lugar.trim();
  const faltaPiso = productos.some((p) => !p.id_piso);

  const añadirP = () => {
    if (faltaLugar) { avisar("Pon primero el lugar de la compra."); return; }
    if (faltaPiso) { avisar("Elige el piso de cada producto antes de añadir otro."); return; }
    setProductos((prev) => [...prev, productoVacio()]);
  };

  const guardar = async () => {
    if (faltaLugar) { avisar("El lugar de la compra es obligatorio."); return; }
    if (faltaPiso) { avisar("Elige el piso de cada producto."); return; }
    const ok = await confirmar(`¿Guardar esta compra en ${lugar} con ${productos.length} producto(s)?`);
    if (!ok) return;
    onGuardar({ id: datos.id, lugar, fecha, productos });
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <h3>🧾 {datos.id ? "Editar compra" : "Nueva compra"}</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 auto" }}>
            <label>Lugar *</label>
            <input value={lugar} onChange={(e) => setLugar(e.target.value)} placeholder="Ferretería / tienda..." autoFocus />
          </div>
          <div>
            <label>Fecha</label>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <b>Productos</b>
          {productos.map((p, i) => (
            <ProductoFila key={i} p={p} onChange={(campo, val) => setP(i, campo, val)} onQuitar={() => quitarP(i)} quitable={productos.length > 1} />
          ))}
          <button className="btn" style={{ marginTop: 8 }} onClick={añadirP}>+ Añadir producto</button>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

function ProductoFila({ p, onChange, onQuitar, quitable }) {
  const [buscaPiso, setBuscaPiso] = useState(p.id_piso ? `${p.id_piso} ${p.piso_codigo || ""}`.trim() : "");

  const onBusca = (v) => {
    setBuscaPiso(v);
    onChange("id_piso", null);
    onChange("piso_codigo", "");
  };
  const elegirPiso = (x) => {
    onChange("id_piso", x.id_origen);
    onChange("piso_codigo", x.codigo);
  };

  return (
    <div className="componente-row activo" style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ minWidth: 170 }}>
          <label style={{ fontSize: 11 }}>Piso *</label>
          <PisoAutocomplete value={buscaPiso} placeholder="ID o abreviatura..."
                            className={p.id_piso ? "input-ok" : ""}
                            onChange={onBusca} onSelect={elegirPiso} />
        </div>
        <div>
          <label style={{ fontSize: 11 }}>Tipo</label>
          <select value={p.tipo} onChange={(e) => onChange("tipo", e.target.value)}>
            <option value="portal">Portal</option>
            <option value="piso">Piso</option>
            <option value="buzon">Buzón</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11 }}>Mecanismo</label>
          <select value={p.mecanismo} onChange={(e) => onChange("mecanismo", e.target.value)}>
            <option value="fisica">Física</option>
            <option value="magnetica">Magnética</option>
          </select>
        </div>
        <div style={{ maxWidth: 70 }}>
          <label style={{ fontSize: 11 }}>Unidades</label>
          <input type="number" step="1" min="1" value={p.unidades ?? 1} onChange={(e) => onChange("unidades", e.target.value)} />
        </div>
        <div style={{ maxWidth: 110 }}>
          <label style={{ fontSize: 11 }}>Precio/ud (€) — opcional</label>
          <input type="number" step="0.01" min="0" value={p.precio ?? ""} onChange={(e) => onChange("precio", e.target.value)} />
        </div>
        {quitable && <button className="btn btn-mini btn-danger" onClick={onQuitar}>Quitar</button>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ DEPARTAMENTOS
function Departamentos() {
  const [rows, setRows] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const { avisar } = useDialog();
  const cargar = () => api.get("/departamentos").then(({ data }) => setRows(data || []));
  useEffect(() => { cargar(); }, []);
  useActualizarGlobal(cargar);

  const crear = async () => {
    if (!nuevo.trim()) return;
    await api.post("/departamentos/crear", { nombre: nuevo.trim() });
    setNuevo(""); cargar();
  };
  const borrar = async (nombre) => {
    const { data } = await api.post("/departamentos/borrar", { nombre });
    if (data.ok) cargar();
    else avisar(data.error || "No se pudo borrar.");
  };

  const campos = [["nombre", "Nombre", (d) => d]];
  const { filtros, setFiltro, limpiar, filtradas } = useFiltroColumnas(rows, campos);

  return (
    <div className="card">
      <PanelHead title="🏷️ Departamentos" count={filtradas.length} total={rows.length} onLimpiar={limpiar} />
      <div className="row">
        <input placeholder="Nuevo departamento" value={nuevo} onChange={(e) => setNuevo(e.target.value)} />
        <button className="btn btn-primary" onClick={crear}>Añadir</button>
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <FilterHead campos={campos} filtros={filtros} setFiltro={setFiltro} extra={<th></th>} />
          </thead>
          <tbody>
            {filtradas.map((d) => (
              <tr key={d}>
                <td className="strong">{d}</td>
                <td className="acc-cell acc-right">
                  <button className="btn btn-danger" onClick={() => borrar(d)}>Borrar</button>
                </td>
              </tr>
            ))}
            {filtradas.length === 0 && <tr><td colSpan={2} className="empty">🗂️ Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
